import { client, getToken } from "./client";

function zeroBasedPage(pagination: number): number {
  return Math.max(0, pagination - 1);
}

export class BrightMarketApi {
  static async getOrganizations() {
    const response = await client.get("/api/organizations?size=100");
    const organizations = response.data._embedded;
    return organizations;
  }

  static async organizationExists(name: string) {
    const response = await client.get(`/api/organizations/exists/${encodeURIComponent(name)}`);
    return response.data;
  }

  static async searchOrganizations(name: string) {
    const response = await client.get(`/api/organizations/search?name=${encodeURIComponent(name)}`, {
      headers: {
        Authorization: getToken(),
      },
    });
    return response.data.data;
  }

  static async getUsers(
    organizationId: string,
    filters: {
      email?: string;
      name?: string;
      businessUnitId?: string;
      rolId?: number;
      branchOfficeId?: number;
    }
  ) {
    const { name, email, rolId, branchOfficeId, businessUnitId } = filters;
    const response = await client.get(
      `/api/users/search/findByOrganizationId?id=${organizationId}${
        email ? `&email=${email}` : ""
      }${name ? `&name=${name}` : ""}${
        businessUnitId ? `&businessUnitId=${businessUnitId}` : ""
      }${rolId ? `&rolId=${rolId}` : ""}${
        branchOfficeId ? `&branchOfficeId=${branchOfficeId}` : ""
      }`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    const users = response.data._embedded;

    return users;
  }

  static async getUser() {
    const response = await client.get(`/api/users/`, {
      headers: {
        Authorization: getToken(),
      },
    });
    return response.data.data;
  }

  static async getUserWithVersion() {
    const response = await client.get(`/api/users/`, {
      headers: {
        Authorization: getToken(),
      },
    });
    return {
      user: response.data.data,
      apiVersion: response.headers["x-api-version"] ?? null,
    };
  }

  static async registerOrganization(
    organizationName: string,
    username: string,
    password: string
  ) {
    const formData = new FormData();
    formData.append("organizationName", organizationName);
    formData.append("userEmail", username);
    formData.append("password", password);
    const response = await client.post("/api/organizations/register", formData);

    return response;
  }

  static async login(
    username: string,
    password: string,
    organizationName: string
  ) {
    const response = await client.post("/api/auth/login", {
      email: username,
      password,
      organizationName,
    });

    return response;
  }

  static async loginSuperAdmin(organizationName: string) {
    const response = await client.post(
      "/api/auth/loginSuperAdmin",
      {
        organizationName,
      },
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async validateEmail(token: string) {
    const response = await client.get(`/api/auth/confirmToken?token=${token}`);
    return response;
  }

  static async confirmUserAndSetPassword(token: string, password: string) {
    const formData = new FormData();
    formData.append("token", token);
    formData.append("password", password);
    const response = await client.post(`/api/auth/confirm`, formData);
    return response;
  }

  static async confirmUser(token: string) {
    const response = await client.get(`/api/auth/confirm?token=${token}`);
    return response;
  }

  static async resendValidateEmail(email: string, organizationName: string) {
    const formData = new FormData();
    formData.append("organizationName", organizationName);
    formData.append("userEmail", email);

    const response = await client.post(
      `/api/auth/resendUserValidation`,
      formData,
      {
        headers: {
        },
      }
    );

    return response;
  }

  static async sendForgotPassword(email: string, organizationName: string) {
    const formData = new FormData();
    formData.append("organizationName", organizationName);
    formData.append("userEmail", email);

    const response = await client.post(`/api/auth/resetPasswordLink`, formData);

    return response;
  }

  static async sendResetPassword(password: string, token: string) {
    const formData = new FormData();
    formData.append("newPassword", password);
    formData.append("token", token);

    const response = await client.post(`/api/auth/resetPassword`, formData);

    return response;
  }

  static async existsOrganizationByName(organizationName: string) {
    const response = await client.get(
      `/api/organizations/search/existsByName?name=${organizationName}`
    );

    return response;
  }

  static async createUser(
    username: string,
    roles: any,
    businessUnitsIds: any,
    branchOfficesIds: any
  ) {
    const formData = new FormData();
    formData.append("userEmail", username);
    if (roles?.length) formData.append("roleIds", roles);
    formData.append("businessUnitsIds", businessUnitsIds);
    formData.append("branchOfficesIds", branchOfficesIds);
    const response = await client.post("/api/users/create", formData, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response;
  }

  static async deleteUser(userId: number) {
    const response = await client.delete(`/api/users/${userId}`, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response;
  }

  static async fetchOrganizationByName(
    organizationName: string,
    authToken?: string
  ) {
    const response = await client.get(
      `/api/organizations/search/findByName?name=${organizationName}&projection=organizationProjection`,
      {
        headers: {
          Authorization: authToken ?? getToken(),
        },
      }
    );

    return response;
  }

  static async fetchOrganizationById(
    organizationId: string,
    authToken?: string
  ) {
    const response = await client.get(`/api/organizations/${organizationId}`, {
      headers: {
        Authorization: authToken ?? getToken(),
      },
    });

    return response;
  }

  static async updateOrganization(
    organizationId: string,
    realName: string,
    businessName: string,
    rut: string,
    address: string
  ) {
    const response = await client.put(
      `/api/organizations/${organizationId}`,
      {
        realName,
        businessName,
        rut,
        address,
      },
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async updateOrganizationClientValidationStats(
    organizationId: string,
    payload: {
      enabled: boolean;
      refreshFrequency: "HOURLY" | "DAILY";
      dailyHour: number;
      dailyMinute?: number;
      hourlyMinute?: number;
    }
  ) {
    const response = await client.put(
      `/api/organizations/${organizationId}/client-validation-stats/settings`,
      payload,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    return response;
  }

  static async getOrganizationClientValidationStatsSettings(organizationId: string) {
    const response = await client.get(
      `/api/organizations/${organizationId}/client-validation-stats/settings`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    return response;
  }

  static async listOrganizationsClientValidationStatsSettings(
    page: number,
    size: number,
    q?: string
  ) {
    const response = await client.get(`/api/organizations/client-validation-stats/settings`, {
      params: { page, size, q },
      headers: {
        Authorization: getToken(),
      },
    });
    return response;
  }

  static async recomputeOrganizationClientValidationStats(organizationId: string) {
    const response = await client.post(
      `/api/organizations/${organizationId}/client-validation-stats/recompute`,
      {},
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    return response;
  }

  static async getOrganizationClientValidationStatsStatus(organizationId: string) {
    const response = await client.get(
      `/api/organizations/${organizationId}/client-validation-stats/status`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    return response;
  }

  static async previewClientContactStorageMigration(organizationId: string) {
    return client.get(
      `/api/organizations/${organizationId}/client-contact-storage-migration/preview`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
  }

  static async migrateClientContactStorage(
    organizationId: string,
    confirmation: string
  ) {
    return client.post(
      `/api/organizations/${organizationId}/client-contact-storage-migration`,
      { confirmation },
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
  }

  static async getClientContactStorageMigrationStatus(
    organizationId: string
  ) {
    return client.get(
      `/api/organizations/${organizationId}/client-contact-storage-migration/status`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
  }

  static async getRoles(
    organizationId: string,
    pagination: number,
    filters: { name?: string; privilegeId?: number }
  ) {
    const { name, privilegeId } = filters;
    const response = await client.get(
      `/api/roles/search/findByOrganizationId?organizationId=${organizationId}&projection=roleProjection&size=10&page=${
        zeroBasedPage(pagination)
      }${name ? `&name=${name}` : ""}${
        privilegeId ? `&privilegeId=${privilegeId}` : ""
      }`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    const roles = response.data._embedded;
    const { totalElements } = response.data.page;

    return { roles, totalElements };
  }

  static async createRol(name: string, description: string, privileges: any) {
    const formData = new FormData();
    formData.append("name", name);
    formData.append("description", description);
    formData.append("privilegesId", privileges);

    const response = await client.post("/api/roles/", formData, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response;
  }

  static async getPrivileges() {
    const response = await client.get(
      `/api/privileges/search/getAll?projection=privilegeProjection`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    const privileges = response.data._embedded;

    return privileges;
  }

  static async getAllRoles(organizationId: string) {
    const response = await client.get(
      `/api/roles/search/getAllByOrganizationId?organizationId=${organizationId}&projection=roleProjection`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    const roles = response.data._embedded;

    return roles;
  }

  static async deleteRol(rolId: number) {
    const response = await client.delete(`/api/roles/${rolId}`, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response;
  }

  static async editRol(
    rolId: number,
    name: string,
    description: string,
    privileges: any
  ) {
    const formData = new FormData();
    formData.append("name", name);
    formData.append("description", description);
    formData.append("privilegesId", privileges);

    const response = await client.put(`/api/roles/${rolId}`, formData, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response;
  }

  static async getCurrencies() {
    const response = await client.get(
      `/api/currencies/search/getAll?projection=currencyProjection`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    const currencies = response.data._embedded;

    return currencies;
  }
  static async getLanguages() {
    const response = await client.get(
      `/api/languages/search/getAll?projection=languageProjection`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    const languages = response.data._embedded;

    return languages;
  }

  static async getChannelsTypes() {
    const response = await client.get(
      `/api/channelTypes/search/getAll?projection=channelTypeProjection`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    const channelTypes = response.data._embedded;

    return channelTypes;
  }

  static async getBusinessUnits(organizationId: string, pagination: number) {
    const response = await client.get(
      `/api/businessUnits/search/findByOrganizationId?organizationId=${organizationId}&projection=businessUnitProjection&size=10&page=${
        zeroBasedPage(pagination)
      }`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    const businessUnits = response.data._embedded;
    const { totalElements } = response.data.page;

    return { businessUnits, totalElements };
  }

  static async getAllBusinessUnits() {
    const response = await client.get(`/api/businessUnits/getAll`, {
      headers: {
        Authorization: getToken(),
      },
    });
    return response.data;
  }

  static async createBusinessUnit(
    name: string,
    languageId: number,
    currencyIds: number[]
  ) {
    const formData = new FormData();
    formData.append("name", name);
    if (languageId) formData.append("languageId", languageId.toString());
    if (currencyIds?.length) {
      currencyIds.forEach((id) => formData.append("currencyIds", id.toString()));
    }

    const response = await client.post("/api/businessUnits/", formData, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response;
  }

  static async createChannel(
    businessUnitId: number,
    currenciesIds: any,
    channelTypeId: number
  ) {
    const formData = new FormData();
    if (currenciesIds) formData.append("currenciesIds", currenciesIds);
    formData.append("channelTypeId", channelTypeId.toString());

    const response = await client.post(
      `/api/businessUnits/${businessUnitId}/createChannel`,
      formData,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async getAllChannelsByBusinessUnits(
    organizationId: string,
    businessUnitsIds: any
  ) {
    let bus = "";
    businessUnitsIds.forEach((id: any) => {
      bus = bus + `${id},`;
    });
    const response = await client.get(
      `/api/channels/search/findByOrganizationIdAndBussinesUnitIds?organizationId=${organizationId}&businessUnitIds=${businessUnitsIds}&projection=ChannelWithBusinessUnitProjection`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    const channels = response.data._embedded;
    return channels;
  }

  static async getChannels(organizationId: string, pagination: number) {
    const response = await client.get(
      `/api/channels/search/findByOrganizationId?organizationId=${organizationId}&projection=ChannelWithBusinessUnitProjection&size=10&page=${pagination}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    const channels = response.data._embedded;
    const { totalElements } = response.data.page;

    return { channels, totalElements };
  }

  static async getAllChannels() {
    const response = await client.get(`/api/businessUnits/channel/getAll`, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response.data;
  }

  static async deleteChannel(channelId: number) {
    const response = await client.delete(`/api/channels/${channelId}`, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response;
  }

  static async editBusinessUnit(
    businessUnitId: number,
    businessUnit: {
      name?: string;
      languageId?: string;
      currencyIds?: number[];
      targetMargin?: number;
    }
  ) {
    const { name, languageId, currencyIds, targetMargin } = businessUnit;
    const formData = new FormData();
    if (name) formData.append("name", name);
    if (languageId) formData.append("languageId", languageId.toString());
    if (currencyIds?.length) {
      currencyIds.forEach((id) => formData.append("currencyIds", id.toString()));
    }
    if (targetMargin !== undefined && targetMargin !== null)
      formData.append("targetMargin", targetMargin.toString());

    const response = await client.put(
      `/api/businessUnits/${businessUnitId}`,
      formData,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async editChannel(
    channelId: number,
    type: string,
    currenciesIds: any
  ) {
    const formData = new FormData();
    formData.append("channelTypeId", type);
    if (currenciesIds) formData.append("currenciesIds", currenciesIds);

    const response = await client.put(
      `/api/businessUnits/channel/${channelId}`,
      formData,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async deleteBusinessUnit(businessUnitId: number) {
    const response = await client.delete(
      `/api/businessUnits/${businessUnitId}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }
  static async editUser(
    userId: number,
    name: string,
    lastName: string,
    roles: any,
    businessUnitsIds: any,
    branchOfficesIds: any
  ) {
    const formData = new FormData();
    formData.append("name", name ?? "");
    formData.append("lastname", lastName ?? "");
    if (roles?.length) formData.append("roleIds", roles);
    formData.append("businessUnitsIds", businessUnitsIds);
    formData.append("branchOfficesIds", branchOfficesIds);

    const response = await client.put(`/api/users/${userId}`, formData, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response;
  }

  static async editUserPassword(oldPassword: string, newPassword: string) {
    const formData = new FormData();
    formData.append("oldPassword", oldPassword);
    formData.append("newPassword", newPassword);

    const response = await client.put(`/api/users/password`, formData, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response;
  }

  static async editUserProfile(name: string, lastName: string) {
    const formData = new FormData();
    formData.append("name", name);
    formData.append("lastname", lastName);

    const response = await client.put(`/api/users/editMyUser`, formData, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response;
  }

  static async getCategoryById(categoryId: string) {
    const response = await client.get(`/api/categories/${categoryId}`, {
      headers: {
        Authorization: getToken(),
      },
    });
    return response.data;
  }

  static async getCategoriesByOrganization(
    organizationId: string,
    pagination: number
  ) {
    const response = await client.get(
      `/api/categories/search/findByOrganizationId?organizationId=${organizationId}&projection=categoryProjection&size=10&page=${
        zeroBasedPage(pagination)
      }`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    const categories = response.data._embedded;
    const { totalElements } = response.data.page;

    return { categories, totalElements };
  }

  static async getCategoriesFiltered(name: string) {
    const response = await client.get(
      `/api/categories/query?name=${name}&page=0&size=10`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response.data;
  }

  static async getSuppliersFiltered(
    pagination: number,
    filters: {
      name?: string;
      businessUnitId?: string;
      externalId?: string;
      articleId?: string;
    }
  ) {
    const { name, businessUnitId, articleId, externalId } = filters;

    const response = await client.get(
      `/api/suppliers/query?${name ? `&name=${name}` : ""}${
        externalId ? `&externalId=${externalId}` : ""
      }${businessUnitId ? `&businessUnitId=${businessUnitId}` : ""}${
        articleId ? `&articleId=${articleId}` : ""
      }&page=${pagination}&size=10`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    const suppliers = response.data.data.content;
    const { totalElements } = response.data.data;

    return { suppliers, totalElements };
  }

  static async getBranchOfficesFiltered(
    pagination: number,
    filters: {
      name?: string;
      externalId?: string;
      businessUnitId?: string;
    }
  ) {
    const { name, businessUnitId, externalId } = filters;

    const response = await client.get(
      `/api/branchOffices/query?${name ? `&name=${name}` : ""}${
        externalId ? `&externalId=${externalId}` : ""
      }${
        businessUnitId ? `&businessUnitId=${businessUnitId}` : ""
      }&page=${pagination}&size=10`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    const branchoffices = response.data.data.content;
    const { totalElements } = response.data.data;

    return { branchoffices, totalElements };
  }

  static async getClientSegmentsFiltered(
    pagination: number,
    filters: {
      name?: string;
      description?: string;
      minMembers?: number;
      maxMembers?: number;
      status?: boolean;
      origin?: string;
      startDate?: string;
      endDate?: string;
      clusterId?: string;
    }
  ) {
    const { name, description, minMembers, maxMembers, status, origin, startDate, endDate, clusterId } = filters;
    const response = await client.get(
      `/api/clients/segments/query?page=${zeroBasedPage(pagination)}&size=10${
        name ? `&name=${name}` : ""
      }${description ? `&description=${description}` : ""}${
        minMembers !== undefined && minMembers !== null ? `&minMembers=${minMembers}` : ""
      }${maxMembers !== undefined && maxMembers !== null ? `&maxMembers=${maxMembers}` : ""
      }${status !== undefined ? `&status=${status}` : ""}${
        origin ? `&origin=${origin}` : ""
      }${startDate ? `&startDate=${startDate}` : ""}${
        endDate ? `&endDate=${endDate}` : ""
      }${clusterId ? `&clusterId=${clusterId}` : ""}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response.data;
  }

  static async getClientSegmentsFilteredLite(
    pagination: number,
    filters: {
      name?: string;
      description?: string;
      minMembers?: number;
      maxMembers?: number;
      status?: boolean;
      origin?: string;
      startDate?: string;
      endDate?: string;
      clusterId?: string;
      typeInt?: number;
    }
  ) {
    const { name, description, minMembers, maxMembers, status, origin, startDate, endDate, clusterId, typeInt } = filters;
    const response = await client.get(
      `/api/clients/segments/queryLite?page=${zeroBasedPage(pagination)}&size=10${
        name ? `&name=${name}` : ""
      }${description ? `&description=${description}` : ""}${
        minMembers !== undefined && minMembers !== null ? `&minMembers=${minMembers}` : ""
      }${maxMembers !== undefined && maxMembers !== null ? `&maxMembers=${maxMembers}` : ""
      }${status !== undefined ? `&status=${status}` : ""}${
        origin ? `&origin=${origin}` : ""
      }${startDate ? `&startDate=${startDate}` : ""}${
        endDate ? `&endDate=${endDate}` : ""
      }${clusterId ? `&clusterId=${clusterId}` : ""}${
        typeInt !== undefined && typeInt !== null ? `&typeInt=${typeInt}` : ""
      }`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response.data;
  }

  static async getClientSegmentsFilteredWithCount(
    pagination: number,
    filters: {
      name?: string;
      description?: string;
      minMembers?: number;
      maxMembers?: number;
      status?: boolean;
      origin?: string;
      startDate?: string;
      endDate?: string;
      clusterId?: string;
    }
  ) {
    const { name, description, minMembers, maxMembers, status, origin, startDate, endDate, clusterId } = filters;
    let queryParams = `page=${zeroBasedPage(pagination)}&size=10`;
    
    if (name) queryParams += `&name=${name}`;
    if (description) queryParams += `&description=${description}`;
    if (minMembers !== undefined && minMembers !== null) queryParams += `&minMembers=${minMembers}`;
    if (maxMembers !== undefined && maxMembers !== null) queryParams += `&maxMembers=${maxMembers}`;
    if (status !== undefined) queryParams += `&status=${status}`;
    if (origin) queryParams += `&origin=${origin}`;
    if (startDate) queryParams += `&startDate=${startDate}`;
    if (endDate) queryParams += `&endDate=${endDate}`;
    if (clusterId) queryParams += `&clusterId=${clusterId}`;
    
    const response = await client.get(
      `/api/clients/segments/queryWithCount?${queryParams}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response.data;
  }

  static async getDepositsFiltered(
    pagination: number,
    filters: { name?: string }
  ) {
    const { name } = filters;
    const response = await client.get(
      `/api/deposits/query?${
        name ? `&name=${name}` : ""
      }&page=${pagination}&size=10`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    const deposits = response.data.data.content;
    const { totalElements } = response.data.data;

    return { deposits, totalElements };
  }

  static async getClientSegmentsCount(ids: number[]) {
    const response = await client.get(
      `/api/clients/segments/count?segmentIds=${ids}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response.data;
  }

  static async getSegmentLinkedSuggestions(segmentId: number | string) {
    const response = await client.get(
      `/api/clients/segments/${segmentId}/linked-suggestions`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response.data;
  }

  static async getSegmentClusterComposition(segmentId: number | string) {
    const response = await client.get(
      `/api/clients/segments/${segmentId}/cluster-composition`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response.data;
  }
  static async getBrandsFiltered(name: string) {
    const response = await client.get(
      `/api/brands/query?name=${name}&page=0&size=10`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response.data;
  }
  static async queryArticlesByName(name: string) {
    const response = await client.get(
      `/api/articles/query?name=${name}&page=0&size=10`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response.data;
  }

  static async getBrandById(id: number) {
    const response = await client.get(`/api/brands/${id}`, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response.data;
  }

  static async getPurchaseUnitsFiltered(name: string) {
    const response = await client.get(
      `/api/purchaseUnits/query?name=${name}&page=0&size=10`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response.data;
  }

  static async getCategoriesByFather(
    organizationId: string,
    pagination: number,
    filters: { name?: string; externalId?: string; enable?: number },
    fatherId?: number
  ) {
    const { name, externalId, enable } = filters;

    const response = await client.get(
      `/api/categories/search/findByFatherId?fatherId=${
        fatherId ?? ""
      }&organizationId=${organizationId}&projection=categoryProjection${
        fatherId ? "" : "&size=10"
      }&page=${pagination} ${name ? `&name=${name}` : ""}${
        enable !== undefined ? `&enable=${enable}` : ""
      }${externalId ? `&externalId=${externalId}` : ""}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    const categories = response.data._embedded;
    const { totalElements } = response.data.page;

    return { categories, totalElements };
  }

  static async getRoundingTypes() {
    const response = await client.get(
      `/api/roundingTypes/search/getAll?projection=roundingType`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    const roundingTypes = response.data._embedded;

    return roundingTypes;
  }

  static async createCategory(category: {
    name: string;
    externalId: string;
    description: string;
    fatherId: number;
    roundingTypeId: number;
    enable: boolean;
    businessUnitId: any;
  }) {
    const formData = new FormData();
    formData.append("name", category.name);
    if (category.description)
      formData.append("description", category.description);
    if (category.externalId) formData.append("externalId", category.externalId);
    formData.append("fatherId", category.fatherId.toString());
    if (category.roundingTypeId)
      formData.append("roundingTypeId", category.roundingTypeId.toString());
    formData.append("enable", category.enable.toString());
    formData.append("businessUnitsIds", category.businessUnitId);

    const response = await client.post("/api/categories/", formData, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response;
  }

  static async deleteCategory(categoryId: number) {
    const response = await client.delete(`/api/categories/${categoryId}`, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response;
  }

  static async editCategory(
    categoryId: number,
    name: string,
    externalId: string,
    description: string,
    fatherId: number,
    roundingTypeId: number,
    enable: boolean,
    businessUnitId: any
  ) {
    const formData = new FormData();
    formData.append("name", name);
    if (externalId) formData.append("externalId", externalId);
    if (description) formData.append("description", description);
    formData.append("fatherId", fatherId.toString());
    if (roundingTypeId)
      formData.append("roundingTypeId", roundingTypeId.toString());
    formData.append("enable", enable.toString());
    formData.append("businessUnitsIds", businessUnitId);

    const response = await client.put(
      `/api/categories/${categoryId}`,
      formData,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async getAllCategories() {
    const response = await client.get(`/api/categories/getAll`, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response.data;
  }

  static async getSkusByBranchOfficeId(
    branchOfficeId: number,
    pagination: number
  ) {
    const response = await client.get(
      `/api/articles/sku/findByBranchOfficeId?size=10&page=${pagination}&branchOfficeId=${branchOfficeId}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    const skus = response.data.data.content;
    const { totalElements } = response.data.data;

    return { skus, totalElements };
  }

  static async getBrandsByBranchOfficeId(
    entity: {
      branchOfficeId?: number;
      depositId?: number;
    },
    pagination: number
  ) {
    const { branchOfficeId, depositId } = entity;
    const response = await client.get(
      `/api/articles/sku/findBrandsDistinctBrandsByBranchOfficeIdOrDepositIdAndOrganizationId?size=10&page=${pagination}
      ${branchOfficeId ? `&branchOfficeId=${branchOfficeId}` : ""}${
        depositId ? `&depositId=${depositId}` : ""
      }
      `,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    const brands = response.data.data.content;
    const { totalElements } = response.data.data;

    return { brands, totalElements };
  }

  static async getBrandsCatalogue(
    pagination: number,
    filters?: { skuId: number; articleId: number }
  ) {
    const response = await client.get(
      `/api/articles/sku/findBrandsDistinctBrandsByOrganizationIdBrandCategoryArticleSkuWithStock?size=10&page=${pagination}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    const brands = response.data.data.content;
    const { totalElements } = response.data.data;

    return { brands, totalElements };
  }

  static async getCategoriesByBrandCatalogue(
    brandId: number,
    pagination: number
  ) {
    const response = await client.get(
      `/api/articles/sku/findCategoriesDistinctBrandsByOrganizationIdBrandIdCategoryIdArticleIdSkuIdWithStock?size=10&page=${pagination}&brandId=${brandId}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    const categories = response.data.data.content;
    const { totalElements } = response.data.data;

    return { categories, totalElements };
  }

  static async getCategoriesByBrandAndBranchOfficeId(
    entity: {
      branchOfficeId?: number;
      depositId?: number;
    },
    brandId: number,
    pagination: number
  ) {
    const { branchOfficeId, depositId } = entity;

    const response = await client.get(
      `/api/articles/sku/findCategoriesDistinctBrandsByBranchOfficeIdOrDepositAndBrandIdAndOrganizationId?size=10&page=${pagination}${
        branchOfficeId ? `&branchOfficeId=${branchOfficeId}` : ""
      }${depositId ? `&depositId=${depositId}` : ""}&brandId=${brandId}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    const categories = response.data.data.content;
    const { totalElements } = response.data.data;

    return { categories, totalElements };
  }

  static async getSkusByBrandCategoryAndBranchOfficeId(
    entity: {
      branchOfficeId?: number;
      depositId?: number;
    },
    brandId: number,
    categoryId: number,
    pagination: number,
    unsupply?: number
  ) {
    const { branchOfficeId, depositId } = entity;

    const response = await client.get(
      `/api/articles/sku/findSkuDistinctBrandsByBranchOfficeIdOrDepositIdAndBrandIdAndCategoryIdAndOrganizationId?size=10&page=${pagination}${
        branchOfficeId ? `&branchOfficeId=${branchOfficeId}` : ""
      }${
        depositId ? `&depositId=${depositId}` : ""
      }&brandId=${brandId}&categoryId=${categoryId}${
        unsupply !== undefined ? `&unsupply=${unsupply}` : ""
      }`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    const skus = response.data.data.content;
    const { totalElements } = response.data.data;

    return { skus, totalElements };
  }
  static async getSkusByBrandCategoryCatalogue(
    brandId: number,
    categoryId: number,
    pagination: number,
    unsupply?: number
  ) {
    const response = await client.get(
      `/api/articles/sku/findSkusDistinctBrandsByOrganizationIdBrandIdCategoryIdArticleIdSkuId?size=10&page=${pagination}&brandId=${brandId}&categoryId=${categoryId}${
        unsupply !== undefined ? `&unsupply=${unsupply}` : ""
      }`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    const skus = response.data.data.content;
    const { totalElements } = response.data.data;

    return { skus, totalElements };
  }

  static async getArticlesByBrandCategoryCatalogue(
    brandId: number,
    categoryId: number,
    pagination: number,
    unsupply?: number
  ) {
    const response = await client.get(
      `/api/articles/article/findArticlesDistinctBrandsByOrganizationIdBrandIdCategoryIdArticleIdSkuId?size=10&page=${pagination}&brandId=${brandId}&categoryId=${categoryId}${
        unsupply !== undefined ? `&unsupply=${unsupply}` : ""
      }`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    const articles = response.data.data.content;
    const { totalElements } = response.data.data;

    return { articles, totalElements };
  }

  static async getArticlesByBranchOfficeOrDepositAndBrandCategoryCatalogue(
    entity: {
      branchOfficeId?: number;
      depositId?: number;
    },
    brandId: number,
    categoryId: number,
    pagination: number,
    unsupply?: number
  ) {
    const { branchOfficeId, depositId } = entity;
    const response = await client.get(
      `/api/articles/article/findArticlesDistinctByBranchOfficeIdOrDepositIdAndBrandIdAndCategoryId?size=10&page=${pagination}${
        branchOfficeId ? `&branchOfficeId=${branchOfficeId}` : ""
      }${depositId ? `&depositId=${depositId}` : ""}&brandId=${brandId}&categoryId=${categoryId}${
        unsupply !== undefined ? `&unsupply=${unsupply}` : ""
      }`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    const articles = response.data.data.content;
    const { totalElements } = response.data.data;

    return { articles, totalElements };
  }

  static async getRepositionOrderRequests(
    pagination: number,
    branchOfficeDestinyId?: number
  ) {
    const response = await client.get(
      `/api/repositionOrders/findSkuArticleQuantityDestiny/?size=10&page=${pagination}${
        branchOfficeDestinyId
          ? `&branchOfficeToId=${branchOfficeDestinyId}`
          : ""
      }`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    const data = response.data.data.content;
    const { totalElements } = response.data.data;
    return { data, totalElements };
  }

  static async getSkusWithStockFiltered(request: {
    pagination: number;
    articleId?: number;
    skuId?: number;
    branchOfficeId: number;
    businessUnitId: string;
  }) {
    const { pagination, articleId, branchOfficeId, skuId, businessUnitId } =
      request;
    const response = await client.get(
      `/api/articles/querySkuStockBranchOffice?&size=10&page=${pagination}${
        articleId ? `&articleId=${articleId}` : ""
      }${
        skuId ? `&skuId=${skuId}` : ""
      }&branchOfficeId=${branchOfficeId}&businessUnitId=${businessUnitId}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    const skus = response.data.data.content;
    const { totalElements } = response.data.data;

    return { skus, totalElements };
  }

  static async getBranchOfficesByName(name: string, organizationId: string) {
    const response = await client.get(
      `/api/branchOffices/search/findByNameContainingAndOrganizationId?organizationId=${organizationId}&projection=branchOfficeProjectionIdName&name=${name}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    const { branchOffices } = response.data._embedded;
    const { totalElements } = response.data.page;

    return { branchOffices, totalElements };
  }

  static async getBranchOfficesByNameFast(
    name: string,
    organizationId: string,
    offset: number,
    size: number
  ) {
    const response = await client.get(
      `/api/branchOffices/searchByName?name=${name}&organizationId=${organizationId}&offset=${offset}&size=${size}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response.data;
  }

  static async getBusinessUnitsByName(name: string, organizationId: string) {
    const response = await client.get(
      `/api/businessUnits/search/findByNameContainingAndOrganizationId?organizationId=${organizationId}&projection=businessUnitProjection&name=${name}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    const { businessUnits } = response.data._embedded;
    const { totalElements } = response.data.page;

    return { businessUnits, totalElements };
  }

  static async getBranchOfficesByNameAndBU(
    name: string,
    organizationId: string,
    businessUnitId: string
  ) {
    const response = await client.get(
      `/api/branchOffices/search/findByNameContainingAndBusinessUnitIdAndOrganizationId?organizationId=${organizationId}&businessUnitId=${businessUnitId}&projection=branchOfficeProjectionIdName&name=${name}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    const { branchOffices } = response.data._embedded;

    return { branchOffices };
  }

  static async getCategoriesPossibleFathers(categoryId: number) {
    const response = await client.get(
      `/api/categories/getPossibleFathers/${categoryId}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response.data;
  }

  static async getArticlesByName(name: string, organizationId: string) {
    const response = await client.get(
      `/api/articles/search/findByNameContainingAndOrganizationId?organizationId=${organizationId}&projection=articleIdNameStockProjection&name=${name}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    const { articles } = response.data._embedded;
    const { totalElements } = response.data.page;

    return { articles, totalElements };
  }

  static async getArticlesByOrganization(
    organizationId: string,
    pagination: number
  ) {
    const response = await client.get(
      `/api/articles/search/findByOrganizationId?organizationId=${organizationId}&projection=articleProjection&size=10&page=${pagination}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    const articles = response.data._embedded;
    const { totalElements } = response.data.page;

    return { articles, totalElements };
  }

  static async getArticlesFiltered(
    organizationId: string,
    pagination: number,
    filters: {
      externalId?: string;
      id?: string;
      categoryId?: string;
      brandId?: number;
      articleName?: string;
      businessUnitId?: string;
    },
    onlyNameId?: boolean,
    projection?: string
  ) {
    const { id, externalId, categoryId, articleName, businessUnitId, brandId } =
      filters;
    const response = await client.get(
      `/api/articles/search/queryArticles?organizationId=${organizationId}&projection=${
        projection
          ? projection
          : onlyNameId
          ? "articleIdNameProjection"
          : "articleProjection"
      }&size=10&page=${pagination}${
        externalId ? `&externalId=${externalId}` : ""
      }${id ? `&id=${id}` : ""}${
        categoryId ? `&categoryId=${categoryId}` : ""
      }${articleName ? `&name=${articleName}` : ""}${
        brandId ? `&brandId=${brandId}` : ""
      }${businessUnitId ? `&businessUnitId=${businessUnitId}` : ""}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    const articles = response.data._embedded;
    const { totalElements } = response.data.page;

    return { articles, totalElements };
  }

  static async getSkusFiltered(
    organizationId: string,
    pagination: number,
    filters: {
      externalId?: string;
      id?: string;
      categoryId?: string;
      articleId?: string;
      articleName?: string;
      skuName?: string;
    }
  ) {
    const { id, externalId, categoryId, articleName, articleId, skuName } =
      filters;
    const response = await client.get(
      `/api/articles/querySkus?organizationId=${organizationId}&projection=articleProjection&size=10&page=${pagination}${
        externalId ? `&externalId=${externalId}` : ""
      }${id ? `&id=${id}` : ""}${
        categoryId ? `&categoryId=${categoryId}` : ""
      }${articleId ? `&articleId=${articleId}` : ""}${
        skuName ? `&name=${skuName}` : ""
      }${articleName ? `&articleName=${articleName}` : ""}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    const skus = response.data.data.content;
    const { totalElements } = response.data.data;

    return { skus, totalElements };
  }

  static async getStockBreaks(
    pagination: number,
    filters: {
      articleId?: string;
      branchOfficeId?: string;
      minStock?: string;
      maxStock?: string;
    }
  ) {
    const { minStock, maxStock, articleId, branchOfficeId } = filters;
    const response = await client.get(
      `/api/repositionOrders/queryStockout?size=10&page=${pagination}${
        minStock ? `&minStock=${minStock}` : ""
      }${maxStock ? `&maxStock=${maxStock}` : ""}${
        articleId ? `&articleId=${articleId}` : ""
      }${branchOfficeId ? `&branchOfficeId=${branchOfficeId}` : ""}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    const data = response.data.data.content;
    const { totalElements } = response.data.data;

    return { data, totalElements };
  }

  static async getStockAdjustments(
    stockId: string,
    type: "deposits" | "branchOffices",
    pagination: number
  ) {
    const response = await client.get(
      `/api/${type}/stockAdjustments/${stockId}?size=10&page=${pagination}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    const data = response.data.data.content;
    const { totalElements } = response.data.data;

    return { data, totalElements };
  }

  static async getArticlesWithStock(
    pagination: number,
    order = "asc",
    articlesId?: string | null,
    categoriesId?: string | null
  ) {
    const response = await client.get(
      `/api/articles/getAllWithStockSort?page=${pagination}&size=20&sortStock=${order}${
        articlesId ? "&articlesIds=" + articlesId : ""
      }${categoriesId ? "&categoriesIds=" + categoriesId : ""}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    const articles = response.data.data.content;
    const { totalElements } = response.data.data;

    return { articles, totalElements };
  }

  static async getArticlesAndSkuWithStock(pagination: number, order = "asc") {
    const response = await client.get(
      `/api/articles/getAllWithStockSortAndSkuStock?page=${
        zeroBasedPage(pagination)
      }&size=20&sortStock=${order}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    const articles = response.data.data.content;
    const { totalElements } = response.data.data;

    return { articles, totalElements };
  }

  static async getAllArticles() {
    const response = await client.get(`/api/articles/getAll`, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response.data;
  }

  static async createArticle(
    article: any,
    suppliers?: any,
    attributes?: any,
    skus?: any
  ) {
    const {
      name,
      externalId,
      description,
      webDescription,
      categoryId,
      brandId,
      cost,
      defaultPrice,
      minimumPrice,
      maximumPrice,
      minimumMargin,
      currencyId,
      costCurrency,
      roundingTypeId,
      enable,
      businessUnitsIds,
      discontinued,
      originType,
      seasons
    } = article;

    const response = await client.post(
      "/api/articles/",
      {
        name,
        externalId,
        description,
        webDescription,
        categoryId,
        brandId,
        cost,
        defaultPrice,
        minimumPrice,
        maximumPrice,
        minimumMargin,
        currencyId,
        costCurrency,
        roundingTypeId,
        enable,
        discontinued,
        skus,
        attributes,
        suppliers,
        originType,
        businessUnitsIds: businessUnitsIds ?? [],
        seasonsIds: seasons ?? []
      },
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async deleteArticle(articleId: number) {
    const response = await client.delete(`/api/articles/${articleId}`, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response;
  }

  static async getSuppliersByOrganization(
    organizationId: string,
    pagination: number
  ) {
    const response = await client.get(
      `/api/suppliers/search/findByOrganizationId?organizationId=${organizationId}&projection=supplierProjection&size=10&page=${
        zeroBasedPage(pagination)
      }`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    const suppliers = response.data._embedded;
    const { totalElements } = response.data.page;

    return { suppliers, totalElements };
  }

  static async deleteSupplier(supplierId: number) {
    const response = await client.delete(`/api/suppliers/${supplierId}`, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response;
  }

  static async createSupplierMultipart(
    supplier: any,
    seasonFileSeasonIds?: number[],
    seasonFiles?: File[]
  ) {
    const formData = new FormData();
    formData.append("supplier", JSON.stringify(supplier));
    if (seasonFileSeasonIds && seasonFileSeasonIds.length > 0) {
      seasonFileSeasonIds.forEach((id) =>
        formData.append("seasonFileSeasonIds", id.toString())
      );
    }
    if (seasonFiles && seasonFiles.length > 0) {
      seasonFiles.forEach((file) => formData.append("seasonFiles", file));
    }
    const response = await client.post(`/api/suppliers/multipart`, formData, {
      headers: {
        Authorization: getToken(),
      },
    });
    return response;
  }

  static async getArticleById(articleId: string) {
    const response = await client.get(`/api/articles/${articleId}`, {
      headers: {
        Authorization: getToken(),
      },
    });
    return response.data;
  }

  static async getSupplierById(supplierId: string) {
    const response = await client.get(`/api/suppliers/${supplierId}`, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response.data;
  }

  static async getAllSuppliers() {
    const response = await client.get(`/api/suppliers/getAll`, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response.data;
  }

  static async addArticleToSupplier(
    articleId: number,
    principal: string,
    supplierId: number,
    purchaseUnitId: number,
    commercialTerm: any
  ) {
    const response = await client.post(
      `/api/suppliers/${supplierId}/articles`,
      {
        articleId,
        principal,
        purchaseUnitId,
        commercialTerm,
      },
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async addContactToSupplier(
    supplierId: number,
    name: string,
    role: string,
    phoneNumber: string,
    email: string
  ) {
    const formData = new FormData();
    formData.append("name", name);
    if (role) formData.append("role", role);
    if (phoneNumber) formData.append("phoneNumber", phoneNumber);
    if (email) formData.append("email", email);

    const response = await client.post(
      `/api/suppliers/${supplierId}/contacts`,
      formData,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    return response;
  }

  static async getAllAttributes() {
    const response = await client.get(`/api/attributes/getAll`, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response.data;
  }

  static async getAttributesFiltered(name: string) {
    const response = await client.get(
      `/api/attributes/query?attribute=${name}&page=0&size=10`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response.data;
  }

  static async getAttributes(
    organizationId: string,
    pagination: number,
    filters: { attribute?: string; value?: string; businessUnitId?: string }
  ) {
    const { attribute, value, businessUnitId } = filters;

    const response = await client.get(
      `/api/attributes/search/findByOrganizationId?organizationId=${organizationId}&projection=atributeProjection&size=10&page=${
        zeroBasedPage(pagination)
      }${attribute ? `&attribute=${attribute}` : ""}${
        businessUnitId ? `&businessUnitId=${businessUnitId}` : ""
      }${value ? `&value=${value}` : ""}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    const attributes = response.data._embedded;
    const { totalElements } = response.data.page;

    return { attributes, totalElements };
  }

  static async deleteAttribute(attributeId: number) {
    const response = await client.delete(`/api/attributes/${attributeId}`, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response;
  }

  static async createAttribute(
    name: string,
    businessUnitsIds: any,
    enums: any
  ) {
    const formData = new FormData();
    formData.append("attribute", name);
    formData.append("businessUnitsIds", businessUnitsIds);
    formData.append("enums", enums);
    formData.append("isEnum", "true");

    const response = await client.post("/api/attributes/", formData, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response;
  }

  static async editAttribute(
    attributeId: number,
    name: string,
    businessUnitsIds: any,
    enums: any
  ) {
    const formData = new FormData();
    formData.append("attribute", name);
    formData.append("businessUnitsIds", businessUnitsIds);
    formData.append("isEnum", "true");
    formData.append("enums", enums);

    const response = await client.put(
      `/api/attributes/${attributeId}`,
      formData,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  // JSON edit removed in favor of multipart

  static async editSupplierMultipart(
    supplierId: string,
    supplier: any,
    seasonFileSeasonIds?: number[],
    seasonFiles?: File[]
  ) {
    const formData = new FormData();
    formData.append("supplier", JSON.stringify(supplier));
    if (seasonFileSeasonIds && seasonFileSeasonIds.length > 0) {
      seasonFileSeasonIds.forEach((id) =>
        formData.append("seasonFileSeasonIds", id.toString())
      );
    }
    if (seasonFiles && seasonFiles.length > 0) {
      seasonFiles.forEach((file) => formData.append("seasonFiles", file));
    }
    const response = await client.put(
      `/api/suppliers/${supplierId}/multipart`,
      formData,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    return response;
  }

  static async deleteSupplierSeasonCatalogueFile(
    supplierId: string,
    seasonId: number
  ) {
    const response = await client.delete(
      `/api/suppliers/${supplierId}/seasonCatalogueFiles/${seasonId}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    return response;
  }

  static async editArticle(article: {
    articleId: string;
    name: string;
    externalId: string;
    description: string;
    webDescription: string;
    originType: string;
    categoryId: number;
    brandId: number;
    currencyId: number;
    costCurrency: number;
    roundingTypeId: number;
    cost: number;
    defaultPrice: number;
    minimumPrice: number;
    maximumPrice: number;
    minimumMargin: number;
    enable: boolean;
    discontinued: boolean;
    businessUnitsIds: any;
  }) {
    const {
      articleId,
      name,
      externalId,
      description,
      webDescription,
      categoryId,
      brandId,
      currencyId,
      roundingTypeId,
      cost,
      defaultPrice,
      minimumPrice,
      maximumPrice,
      minimumMargin,
      enable,
      discontinued,
      businessUnitsIds,
      originType,
      costCurrency,
    } = article;

    const formData = new FormData();
    formData.append("name", name);
    if (externalId) formData.append("externalId", externalId);
    if (description) formData.append("description", description);
    if (webDescription) formData.append("webDescription", webDescription);
    if (originType) formData.append("originType", originType);
    if (categoryId) formData.append("categoryId", categoryId?.toString());
    if (brandId) formData.append("brandId", brandId.toString());
    if (currencyId) formData.append("currencyId", currencyId?.toString());
    if (costCurrency) formData.append("costCurrency", costCurrency?.toString());
    if (roundingTypeId)
      formData.append("roundingTypeId", roundingTypeId?.toString());
    if (cost) formData.append("cost", cost.toString());
    if (defaultPrice) formData.append("defaultPrice", defaultPrice.toString());
    if (minimumPrice) formData.append("minimumPrice", minimumPrice.toString());
    if (maximumPrice) formData.append("maximumPrice", maximumPrice.toString());
    if (minimumMargin)
      formData.append("minimumMargin", minimumMargin.toString());
    formData.append("enable", enable.toString());
    formData.append("discontinued", discontinued.toString());
    formData.append("businessUnitsIds", businessUnitsIds);

    const response = await client.put(`/api/articles/${articleId}`, formData, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response;
  }

  static async addAttributeToArticle(
    articleId: number,
    attributeId: number,
    enumId: number
  ) {
    const formData = new FormData();
    formData.append("attributeId", attributeId.toString());
    formData.append("idEnum", enumId.toString());

    const response = await client.post(
      `/api/articles/${articleId}/attributeValue`,
      formData,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async addSeasonToArticle(articleId: number, seasonId: number) {
    const formData = new FormData();
    formData.append("seasonId", seasonId.toString());

    const response = await client.post(
      `/api/articles/${articleId}/addSeason`,
      formData,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async addPriceToArticle(
    articleId: number,
    branchOfficeId: number,
    price: string,
    date: any
  ) {
    const formData = new FormData();
    formData.append("branchOfficeId", branchOfficeId.toString());
    formData.append("price", price);
    formData.append("date", date);

    const response = await client.post(
      `/api/articles/${articleId}/addPrice`,
      formData,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async addAttributeToSku(
    skuId: number,
    attributeId: number,
    enumId: number
  ) {
    const formData = new FormData();
    formData.append("attributeId", attributeId.toString());
    formData.append("idEnum", enumId.toString());

    const response = await client.post(
      `/api/articles/sku/${skuId}/attributeValue`,
      formData,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async deleteAttributeFromArticle(articleAttributeId: number) {
    const response = await client.delete(
      `/api/articles/attributeValue/${articleAttributeId}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async deleteSeasonFromArticle(articleSeasonId: number) {
    const response = await client.delete(
      `/api/articles/removeSeason/${articleSeasonId}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async deleteSkuAttributeFromArticle(skuAttributeId: number) {
    const response = await client.delete(
      `/api/articles/sku/attributeValue/${skuAttributeId}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async editAttributeFromArticle(
    articleAttributeId: number,
    value: string
  ) {
    const formData = new FormData();
    formData.append("value", value);

    const response = await client.put(
      `/api/articles/attributeValue/${articleAttributeId}`,
      formData,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async editSkuAttributeFromArticle(
    articleSkuAttributeId: number,
    value: string
  ) {
    const formData = new FormData();
    formData.append("value", value);

    const response = await client.put(
      `/api/articles/sku/attributeValue/${articleSkuAttributeId}`,
      formData,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async deleteContact(contactId: number) {
    const response = await client.delete(
      `/api/suppliers/contacts/${contactId}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async deleteArticleSupplier(articleSupplierId: number) {
    const response = await client.delete(
      `/api/suppliers/articles/${articleSupplierId}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async editContact(
    contactId: number,
    name: string,
    role: string,
    email: string,
    phoneNumber: string
  ) {
    const formData = new FormData();
    formData.append("name", name);
    if (role) formData.append("role", role);
    if (email) formData.append("email", email);
    if (phoneNumber) formData.append("phoneNumber", phoneNumber);

    const response = await client.put(
      `/api/suppliers/contacts/${contactId}`,
      formData,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async editArticleToSupplier(
    id: number,
    articleId: number,
    principal: string,
    purchaseUnitId: number,
    commercialTerm: any
  ) {
    const response = await client.put(
      `/api/suppliers/articles/${id}`,
      {
        articleId,
        principal,
        purchaseUnitId,
        commercialTerm,
      },
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    return response;
  }

  static async createSkuInArticle(
    articleId: string,
    sku: any,
    attributes: any
  ) {
    const {
      name,
      externalId,
      enable,
      heightWithPackaging,
      lengthWithPackaging,
      widthWithPackaging,
      weightWithPackaging,
      height,
      length,
      width,
      weight,
      volumen,
      kit,
      salesUnit,
      storageUnit,
      minimumStock,
      size,
      colour,
      brand,
      cost,
      replenish,
      currencyId,
    } = sku;

    const response = await client.post(
      `/api/articles/${articleId}/sku`,
      {
        name,
        externalId,
        enable,
        heightWithPackaging,
        lengthWithPackaging,
        widthWithPackaging,
        weightWithPackaging,
        height,
        length,
        width,
        weight,
        volumen,
        kit,
        salesUnit,
        storageUnit,
        minimumStock,
        size,
        colourId: colour,
        brand,
        attributes,
        cost,
        replenish,
        currencyId,
      },
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    return response;
  }

  static async deleteSkuFromArticle(articleSkuId: number) {
    const response = await client.delete(`/api/articles/sku/${articleSkuId}`, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response;
  }

  static async editSkuFromArticle(
    articleSkuId: number,
    name: string,
    enable: string,
    heightWithPackaging: number,
    lengthWithPackaging: number,
    widthWithPackaging: number,
    weightWithPackaging: number,
    height: number,
    length: number,
    width: number,
    weight: number,
    volumen: number,
    kit: boolean,
    salesUnit: string,
    storageUnit: string,
    size: string,
    colour: number,
    minimumStock: number,
    externalId: string,
    cost: number,
    replenish: boolean,
    currencyId: number,
    photo1: string,
    photo2: string,
    photo3: string
  ) {
    const formData = new FormData();
    formData.append("name", name);
    formData.append("enable", enable.toString());
    if (heightWithPackaging)
      formData.append("heightWithPackaging", heightWithPackaging.toString());
    if (lengthWithPackaging)
      formData.append("lengthWithPackaging", lengthWithPackaging.toString());
    if (widthWithPackaging)
      formData.append("widthWithPackaging", widthWithPackaging.toString());
    if (weightWithPackaging)
      formData.append("weightWithPackaging", weightWithPackaging.toString());
    if (height) formData.append("height", height.toString());
    if (length) formData.append("length", length.toString());
    if (width) formData.append("width", width.toString());
    if (weight) formData.append("weight", weight.toString());
    if (volumen) formData.append("volumen", volumen.toString());
    formData.append("kit", kit.toString());
    formData.append("replenish", replenish.toString());
    if (salesUnit) formData.append("salesUnit", salesUnit);
    if (storageUnit) formData.append("storageUnit", storageUnit);
    formData.append("size", size ?? "");
    if (colour) formData.append("colourId", colour.toString());
    if (externalId) formData.append("externalId", externalId);
    if (cost) formData.append("cost", cost.toString());
    if (currencyId) formData.append("currencyId", currencyId.toString());
    if (minimumStock) formData.append("minimumStock", minimumStock.toString());
    if (photo1) formData.append("photo1", photo1);
    if (photo2) formData.append("photo2", photo2);
    if (photo3) formData.append("photo3", photo3);

    const response = await client.put(
      `/api/articles/sku/${articleSkuId}`,
      formData,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async editSupplierToArticle(
    id: number,
    supplierId: number,
    principal: string,
    purchaseUnitId: number,
    commercialTerm: any
  ) {
    const response = await client.put(
      `/api/articles/suppliers/${id}`,
      {
        supplierId,
        principal,
        purchaseUnitId,
        commercialTerm,
      },
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    return response;
  }

  static async getBusinessUnitById(businessUnitId: string) {
    const response = await client.get(`/api/businessUnits/${businessUnitId}`, {
      headers: {
        Authorization: getToken(),
      },
    });
    return response.data;
  }

  static async getPricingTypes(organizationId: string, pagination: number) {
    const response = await client.get(
      `/api/pricingTypes/search/findByOrganizationId?organizationId=${organizationId}&projection=pricingTypeProjection&size=10&page=${
        zeroBasedPage(pagination)
      }`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    const pricingTypes = response.data._embedded;
    const { totalElements } = response.data.page;

    return { pricingTypes, totalElements };
  }

  static async createPricingType(code: string, description: string) {
    const formData = new FormData();
    formData.append("code", code);
    if (description) formData.append("description", description);

    const response = await client.post("/api/pricingTypes/", formData, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response;
  }

  static async deletePricingType(pricingTypeId: number) {
    const response = await client.delete(`/api/pricingTypes/${pricingTypeId}`, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response;
  }

  static async editPricingType(
    pricingTypeId: number,
    code: string,
    description: string
  ) {
    const formData = new FormData();
    formData.append("code", code);
    if (description) formData.append("description", description);

    const response = await client.put(
      `/api/pricingTypes/${pricingTypeId}`,
      formData,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async getPromotionTypes(organizationId: string, pagination: number) {
    const response = await client.get(
      `/api/promotionTypes/search/findByOrganizationId?organizationId=${organizationId}&projection=promotionTypeProjection&size=10&page=${
        zeroBasedPage(pagination)
      }`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    const promotionTypes = response.data._embedded;
    const { totalElements } = response.data.page;

    return { promotionTypes, totalElements };
  }

  static async createPromotionType(code: string, name: string) {
    const formData = new FormData();
    formData.append("code", code);
    if (name) formData.append("name", name);

    const response = await client.post("/api/promotionTypes/", formData, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response;
  }

  static async deletePromotionType(promotionTypeId: number) {
    const response = await client.delete(
      `/api/promotionTypes/${promotionTypeId}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async editPromotionType(
    promotionTypeId: number,
    code: string,
    name: string
  ) {
    const formData = new FormData();
    formData.append("code", code);
    if (name) formData.append("name", name);

    const response = await client.put(
      `/api/promotionTypes/${promotionTypeId}`,
      formData,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async deleteBranchOffice(branchOfficeId: number) {
    const response = await client.delete(
      `/api/branchOffices/${branchOfficeId}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async getBranchOfficeById(branchOfficeId: string) {
    const response = await client.get(
      `/api/branchOffices/withArticleCost/${branchOfficeId}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    return response.data;
  }

  static async getBranchOfficeSkusById(
    branchOfficeId: string,
    pagination: number,
    organizationId: string,
    skuName: string
  ) {
    const response = await client.get(
      `/api/skuBranchOffices/search/findByBranchOfficeIdAndBranchOfficeOrganizationIdAndSkuNameContaining?branchOfficeId=${branchOfficeId}&organizationId=${organizationId}&page=${pagination}&size=10&skuName=${skuName}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    const skus = response.data._embedded.skuBranchOffices;
    const { totalElements } = response.data.page;
    return { skus, totalElements };
  }

  static async getDepositsSkusById(
    depositId: string,
    pagination: number,
    organizationId: string,
    skuName: string
  ) {
    const response = await client.get(
      `/api/skuDeposits/search/findByDepositIdAndDepositOrganizationIdAndSkuNameContaining?depositId=${depositId}&organizationId=${organizationId}&page=${pagination}&size=10&skuName=${skuName}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    const skus = response.data._embedded.skuDeposits;
    const { totalElements } = response.data.page;
    return { skus, totalElements };
  }

  static async editBranchOffice(branchOffice: any) {
    const response = await client.put(
      `/api/branchOffices/${branchOffice.id}`,
      branchOffice,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async getAllBranchOfficesTypes() {
    const response = await client.get(`/api/branchOffices/branchOfficeTypes`, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response.data;
  }

  static async createBranchOffice(branchOffice: any) {
    const response = await client.post(`/api/branchOffices/`, branchOffice, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response;
  }

  static async getAcceptedPaymentMethods(
    organizationId: string,
    pagination: number,
    businessUnitId?: string
  ) {
    const response = await client.get(
      `/api/acceptedPaymentMethod/search/findByOrganizationIdAndBusinessUnitId?organizationId=${organizationId}&projection=acceptedPaymentMethodProjection&size=10&page=${
        zeroBasedPage(pagination)
      }${businessUnitId ? `&businessUnitId=${businessUnitId}` : ""}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    const acceptedPaymentMethods = response.data._embedded;
    const { totalElements } = response.data.page;

    return { acceptedPaymentMethods, totalElements };
  }

  static async getAcceptedPaymentMethodById(paymentMethodId: string) {
    const response = await client.get(
      `/api/acceptedPaymentMethod/${paymentMethodId}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    return response.data;
  }

  static async getAllAcceptedPaymentMethods() {
    const response = await client.get(`/api/acceptedPaymentMethod/getAll`, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response.data;
  }

  static async getAcceptedPaymentMethodsTypes() {
    const response = await client.get(`/api/acceptedPaymentMethod/types`, {
      headers: {
        Authorization: getToken(),
      },
    });
    const acceptedPaymentMethodsTypes = response.data;

    return acceptedPaymentMethodsTypes;
  }

  static async getPaymentMethodsByBusinessUnitId(businessUnitId: number) {
    const response = await client.get(
      `/api/acceptedPaymentMethod/getByBussinesUnitId/${businessUnitId}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    const acceptedPaymentMethods = response.data;

    return acceptedPaymentMethods;
  }

  static async createAcceptedPaymentMethod(acceptedPaymentMethod: {
    name: string;
    externalId: string;
    typeId: number;
    feeRangeMin: number;
    feeRangeMax: number;
    businessUnitsIds: any;
  }) {
    const response = await client.post(
      "/api/acceptedPaymentMethod/",
      acceptedPaymentMethod,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async editAcceptedPaymentMethod(
    acceptedPaymentMethodId: number,
    acceptedPaymentMethod: {
      name: string;
      externalId: string;
      typeId: number;
      feeRangeMin: number;
      feeRangeMax: number;
      businessUnitsIds: any;
    }
  ) {
    const response = await client.put(
      `/api/acceptedPaymentMethod/${acceptedPaymentMethodId}`,
      acceptedPaymentMethod,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async deleteAcceptedPaymentMethod(acceptedPaymentMethodId: number) {
    const response = await client.delete(
      `/api/acceptedPaymentMethod/${acceptedPaymentMethodId}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async deleteAcceptedPaymentMethodFromBusinessUnit(
    acceptedPaymentMethodId: number,
    businessUnitId: number
  ) {
    const response = await client.delete(
      `/api/businessUnits/${businessUnitId}/removeAcceptedPaymentMethod/${acceptedPaymentMethodId}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async addAcceptedPaymentMethodToBusinessUnit(
    businessUnitId: number,
    acceptedPaymentMethodId: number
  ) {
    const response = await client.post(
      `/api/businessUnits/${businessUnitId}/addAcceptedPaymentMethod/${acceptedPaymentMethodId}`,
      {},
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async getCompetitors(organizationId: string, pagination: number) {
    const response = await client.get(
      `/api/competitors/search/findByOrganizationId?organizationId=${organizationId}&projection=competitorProjection&size=10&page=${
        zeroBasedPage(pagination)
      }`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    const competitors = response.data._embedded;
    const { totalElements } = response.data.page;

    return { competitors, totalElements };
  }

  static async getCompetitorById(competitorId: string) {
    const response = await client.get(`/api/competitors/${competitorId}`, {
      headers: {
        Authorization: getToken(),
      },
    });
    return response.data;
  }

  static async deleteCompetitor(competitorId: number) {
    const response = await client.delete(`/api/competitors/${competitorId}`, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response;
  }

  static async createCompetitor(
    name: string,
    externalId: string,
    website: string,
    articlesIds: any
  ) {
    const formData = new FormData();
    formData.append("name", name);
    if (externalId) formData.append("externalId", externalId);
    if (website) formData.append("website", website);
    formData.append("articlesIds", articlesIds);

    const response = await client.post("/api/competitors/", formData, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response;
  }

  static async editCompetitor(
    competitorId: string,
    name: string,
    externalId: string,
    website: string,
    articleIds: any
  ) {
    const formData = new FormData();
    formData.append("name", name);
    if (externalId) formData.append("externalId", externalId);
    if (website) formData.append("website", website);
    formData.append("articlesIds", articleIds);

    const response = await client.put(
      `/api/competitors/${competitorId}`,
      formData,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async createSalePoint(
    branchOfficeId: number,
    name: string,
    acceptedPaymentMethodsIds: any,
    externalId?: string
  ) {
    const response = await client.post(
      "/api/branchOffices/salePoint",
      { branchOfficeId, name, acceptedPaymentMethodsIds, externalId },
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async deleteSalePoint(salePointId: number) {
    const response = await client.delete(
      `/api/branchOffices/salePoint/${salePointId}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async editSalePoint(
    salePointId: number,
    name: string,
    acceptedPaymentMethodsIds: any,
    externalId?: string
  ) {
    const response = await client.put(
      `/api/branchOffices/salePoint/${salePointId}`,
      { name, acceptedPaymentMethodsIds, externalId },
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async getDeposits(
    organizationId: string,
    pagination: number,
    businessUnitId: string,
    filters: { name: string; externalId: string }
  ) {
    const { name, externalId } = filters;
    const response = await client.get(
      `/api/deposits/search/findByOrganizationIdAndBusinessUnitId?organizationId=${organizationId}&businessUnitId=${businessUnitId}&projection=depositListProjection&size=10&page=${pagination}${
        name ? `&name=${name}` : ""
      }${externalId ? `&externalId=${externalId}` : ""}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    const deposits = response.data._embedded;
    const { totalElements } = response.data.page;

    return { deposits, totalElements };
  }

  static async createDeposit(deposit: {
    name: string;
    capacity: number;
    externalId: string;
    branchOffices: any;
    address: string;
    latitude: number;
    longitude: number;
    businessUnitsIds: any;
    categoriesIds: any;
    capacityInUnits: number;
    capacityDispatchUnits: number;
    dispatchFrequency: number;
    capacityReception: number;
    storageCostPerUnit: number;
    minimumStock: number;
    maximumStock: number;
  }) {
    const response = await client.post(
      "/api/deposits/",
      {
        ...deposit,
      },
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async deleteDeposit(depositId: number) {
    const response = await client.delete(`/api/deposits/${depositId}`, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response;
  }

  static async editDeposit(
    depositId: number,
    deposit: {
      name: string;
      capacity: number;
      externalId: string;
      branchOffices: any;
      address: string;
      businessUnitsIds: any;
      categoriesIds: any;
      capacityInUnits: number;
      capacityDispatchUnits: number;
      dispatchFrequency: number;
      capacityReception: number;
      storageCostPerUnit: number;
      minimumStock: number;
      maximumStock: number;
      latitude: number;
      longitude: number;
    }
  ) {
    const response = await client.put(
      `/api/deposits/${depositId}`,
      {
        ...deposit,
      },
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async getAllBranchOffices(organizationId: string) {
    const response = await client.get(`/api/branchOffices/listIdNames`, {
      headers: {
        Authorization: getToken(),
      },
    });
    return { branchOffices: response.data.data ?? [] };
  }

  static async getAllBrands(organizationId: string) {
    const response = await client.get(`/api/brands/getAll`, {
      headers: {
        Authorization: getToken(),
      },
    });
    return { brands: response.data.data ?? [] };
  }

  static async getAllBranchOfficesByBu(businessUnitIds: any) {
    let queryParam = "";
    for (let i = 0; i < businessUnitIds.length; i++) {
      queryParam += `businessUnitIds=${businessUnitIds[i]}&`;
    }

    const response = await client.get(
      `/api/branchOffices/getByBusinessUnitIds?${queryParam}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    return response.data.data;
  }

  static async getBranchOffices(organizationId: string, pagination: number) {
    const response = await client.get(
      `/api/branchOffices/search/findByOrganizationIdPage?organizationId=${organizationId}&projection=branchOfficeProjectionIdName&size=10&page=${zeroBasedPage(pagination)}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    const branchOffices = response.data._embedded;
    const { totalElements } = response.data.page;

    return { branchOffices, totalElements };
  }

  static async getDepositById(depositId: string) {
    const response = await client.get(
      `/api/deposits/${depositId}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    return response.data;
  }

  static async getDepositValuedInventory(depositId: string) {
    const response = await client.get(
      `/api/deposits/${depositId}/valuedInventory`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response.data.data;
  }

  static async getDepositSchedules(depositId: string, pagination: number) {
    const response = await client.get(
      `/api/deposits/${depositId}/schedules?size=10&page=${pagination}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    const data = response.data.data.content;
    const { totalElements } = response.data.data;

    return { data, totalElements };
  }

  static async deleteArticleStock(artcileStockId: number, type: string) {
    const response = await client.delete(
      `/api/${type}/deleteArticle/${artcileStockId}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async addArticleStock(
    stock: number,
    skuId: number,
    type: string,
    placeId: number,
    minimumStock?: number,
    leadTime?: number
  ) {
    const formData = new FormData();
    formData.append("stock", stock.toString());
    if (type === "branchOffices") {
      if (leadTime) formData.append("leadTime", leadTime.toString());
      if (minimumStock)
        formData.append("minimumStock", minimumStock.toString());
    }

    const response = await client.post(
      `/api/${type}/${placeId}/addSku/${skuId}`,
      formData,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async editArticleStock(
    articleStockId: number,
    stock: string,
    type: string
  ) {
    const formData = new FormData();
    formData.append("stock", stock);

    const response = await client.put(
      `/api/${type}/editStock/${articleStockId}`,
      formData,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async getPurchaseOrdersByOrganization(
    pagination: number,
    supplierId?: number | null,
    status?: string | null,
    articlesId?: string | null,
    startDate?: string | null,
    endDate?: string | null
  ) {
    const response = await client.get(
      `/api/purchaseOrders/queryRequests?&size=10&page=${pagination}${
        supplierId ? "&supplierId=" + supplierId : ""
      }${status ? "&status=" + status : ""}${
        articlesId ? "&articlesIds=" + articlesId : ""
      }${startDate ? "&startEstimatedDeliveryDate=" + startDate : ""}${
        endDate ? "&endEstimatedDeliveryDate=" + endDate : ""
      }`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    const purchaseOrders = response.data.data.content;
    const { totalElements } = response.data.data;

    return { purchaseOrders, totalElements };
  }

  static async createPurchaseOrder(
    body: any,
    preview: boolean,
    businessUnitId?: string
  ) {
    const response = await client.post(
      `/api/purchaseOrders/${preview ? "preview" : ""}${businessUnitId ?? ""}`,
      body,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async getPurchaseOrderStatics() {
    const response = await client.get(`/api/purchaseOrders/statistics`, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response;
  }

  static async getAllDeposits() {
    const response = await client.get(`/api/deposits/getAll`, {
      headers: {
        Authorization: getToken(),
      },
    });
    return response;
  }

  static async deletePurchaseOrder(purchaseOrderId: number) {
    const response = await client.delete(
      `/api/purchaseOrders/${purchaseOrderId}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async getPurchaseOrderById(purchaseOrderId: string) {
    const response = await client.get(
      `/api/purchaseOrders/${purchaseOrderId}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response.data;
  }

  static async markReceivedOrder(body: any) {
    const response = await client.post(
      `/api/purchaseOrders/markReceived`,
      body,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async markReceivedRepositionOrder(body: any) {
    const response = await client.post(
      `/api/repositionOrders/markReceived`,
      body,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async markSentArticlesRepositionOrder(
    repositionOrderSkuIds: any,
    arriveDate: string
  ) {
    const formData = new FormData();
    formData.append("estimatedDate", arriveDate);
    formData.append("repositionOrderSkuId", repositionOrderSkuIds);
    const response = await client.post(
      `/api/repositionOrders/markAsSent`,
      formData,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async editPurchaseOrderStatus(
    purchaseOrderId: number,
    status: string,
    notCompletedReason?: string
  ) {
    const formData = new FormData();
    formData.append("status", status);
    if (notCompletedReason)
      formData.append("notCompletedReason", notCompletedReason);

    const response = await client.put(
      `/api/purchaseOrders/changeStatus/${purchaseOrderId}`,
      formData,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async getRepositionOrdersByOrganization(query: {
    pagination: number;
    status?: string | null;
    startCreatedDate?: string | null;
    endCreatedDate?: string | null;
    depositTo?: number | null;
    branchOfficeTo?: number | null;
    depositFrom?: number | null;
    branchOfficeFrom?: number | null;
    orderBy?: string;
    businessUnitId?: string;
    articlesIds?: number[];
    skuId?: string | null;
  }) {
    const {
      pagination,
      status,
      startCreatedDate,
      endCreatedDate,
      depositTo,
      branchOfficeTo,
      depositFrom,
      branchOfficeFrom,
      orderBy,
      businessUnitId,
      skuId,
      articlesIds,
    } = query;

    const response = await client.get(
      `/api/repositionOrders/queryRequests?&size=10&page=${pagination}${
        status ? "&status=" + status : ""
      }${startCreatedDate ? "&startCreatedDate=" + startCreatedDate : ""}${
        endCreatedDate ? "&endCreatedDate=" + endCreatedDate : ""
      }${depositTo ? "&depositToId=" + depositTo : ""}${
        branchOfficeTo ? "&branchOfficeToId=" + branchOfficeTo : ""
      }${depositFrom ? "&depositFromId=" + depositFrom : ""}
      ${branchOfficeFrom ? "&branchOfficeFromId=" + branchOfficeFrom : ""}${
        businessUnitId ? "&businessUnitId=" + businessUnitId : ""
      }${articlesIds ? "&articlesIds=" + articlesIds : ""}${
        skuId ? "&skuId=" + skuId : ""
      }
      ${orderBy ? "&orderBy=" + orderBy : ""}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    const repositionOrders = response.data.data.content;
    const { totalElements } = response.data.data;

    return { repositionOrders, totalElements };
  }

  static async deleteRepositionOrder(repositionOrderId: number) {
    const response = await client.delete(
      `/api/repositionOrders/${repositionOrderId}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async editRepositionOrderStatus(
    repositionOrderId: number,
    status: string
  ) {
    const formData = new FormData();
    formData.append("status", status);

    const response = await client.put(
      `/api/repositionOrders/changeStatus/${repositionOrderId}`,
      formData,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async getRepositionOrderStatics() {
    const response = await client.get(`/api/repositionOrders/statistics`, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response;
  }

  static async getRepositionOrderById(repositionOrderId: string) {
    const response = await client.get(
      `/api/repositionOrders/${repositionOrderId}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response.data;
  }

  static async createRepositionOrder(
    body: any,
    preview: boolean,
    businessUnitId?: string
  ) {
    const response = await client.post(
      `/api/repositionOrders/${preview ? "preview" : ""}${
        businessUnitId ?? ""
      }`,
      body,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async createRepositionOrderDraft(
    orders: {
      skuId: number;
      branchOfficeOrDepositFrom: string;
      branchOfficeOrDepositFromId: number;
      branchOfficeOrDepositTo: string;
      branchOfficeOrDepositToId: number;
      quantity: number;
    }[],
    businessUnitId: string
  ) {
    const response = await client.post(
      `/api/repositionOrders/draft/${businessUnitId}/${orders[0].branchOfficeOrDepositToId}`,
      orders,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async createPurchaseOrderDraft(
    orders: {
      skuId: number;
      branchOfficeOrDeposit: string;
      branchOfficeOrDepositId: number;
      supplierId: string;
      quantity: number;
    }[],
    businessUnitId: string
  ) {
    const response = await client.post(
      `/api/purchaseOrders/draft/${businessUnitId}`,
      orders,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async getRepositionOrderDraft(businessUnitId: string) {
    const response = await client.get(
      `/api/repositionOrders/draft/${businessUnitId}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response.data;
  }
  static async getPurchaseOrderDraft(businessUnitId: string) {
    const response = await client.get(
      `/api/purchaseOrders/draft/${businessUnitId}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response.data;
  }

  static async getClients(
    organizationId: string,
    pagination: number,
    filters: {
      clientId?: string;
      idNumber?: string;
      name?: string;
      email?: string;
      mobile?: string;
      segmentId?: string;
      levelId?: string;
      birthStartDate?: string;
      birthEndDate?: string;
      registerStartDate?: string;
      registerEndDate?: string;
    },
    size: number = 10
  ) {
    const {
      clientId,
      idNumber,
      name,
      email,
      mobile,
      segmentId,
      levelId,
      birthEndDate,
      birthStartDate,
      registerStartDate,
      registerEndDate,
    } = filters;

    const response = await client.get(
      `/api/clients/search/findByFilters?organizationId=${organizationId}&projection=clientProjection&size=${size}&page=${
        zeroBasedPage(pagination)
      }${clientId ? `&clientId=${encodeURIComponent(clientId)}` : ""}${idNumber ? `&idNumber=${idNumber}` : ""}${name ? `&name=${encodeURIComponent(name)}` : ""}${email ? `&email=${encodeURIComponent(email)}` : ""}${mobile ? `&mobile=${encodeURIComponent(mobile)}` : ""}${levelId ? `&levelId=${levelId}` : ""}${
        segmentId ? `&segmentId=${segmentId}` : ""
      }${birthStartDate ? `&birthStartDate=${birthStartDate}` : ""}${
        birthEndDate ? `&birthEndDate=${birthEndDate}` : ""
      }${
        registerEndDate ? `&registerEndDate=${registerEndDate}T23:59:59` : ""
      }${
        registerStartDate
          ? `&registerStartDate=${registerStartDate}T00:00:00`
          : ""
      }`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    const clients = response.data._embedded?.clients || [];
    const { totalElements } = response.data.page;
    return { _embedded: { clients }, totalElements };
  }

  /**
   * Search clients by LIKE filters only (clientId, name, email, idNumber, mobile).
   * Uses dedicated backend endpoint so filtering by e.g. only ID works correctly.
   */
  static async getClientsByLikeFilters(
    page: number,
    size: number,
    filters: {
      clientId?: string;
      name?: string;
      email?: string;
      idNumber?: string;
      mobile?: string;
    }
  ) {
    const { clientId, name, email, idNumber, mobile } = filters;
    const searchParams = new URLSearchParams();
    searchParams.append("page", String(page));
    searchParams.append("size", String(size));
    if (clientId) searchParams.append("clientId", clientId);
    if (name) searchParams.append("name", name);
    if (email) searchParams.append("email", email);
    if (idNumber) searchParams.append("idNumber", idNumber);
    if (mobile) searchParams.append("mobile", mobile);
    const response = await client.get(
      `/api/clients/search/byLikeFilters?${searchParams.toString()}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    const clients = response.data._embedded?.clients || [];
    const totalElements = response.data.page?.totalElements ?? 0;
    return { clients, totalElements };
  }

  private static buildClientListFilterQuery(filters: {
    idNumber?: string;
    externalId?: string;
    segmentId?: string;
    levelId?: string;
    clusterId?: string;
    branchOfficeId?: string;
    birthStartDate?: string;
    birthEndDate?: string;
    registerStartDate?: string;
    registerEndDate?: string;
  }) {
    const params = new URLSearchParams();
    const {
      idNumber,
      externalId,
      segmentId,
      levelId,
      clusterId,
      branchOfficeId,
      birthStartDate,
      birthEndDate,
      registerStartDate,
      registerEndDate,
    } = filters;

    if (idNumber) params.set("idNumber", idNumber);
    if (externalId) params.set("externalId", externalId);
    if (segmentId) params.set("segmentId", segmentId);
    if (levelId) params.set("levelId", levelId);
    if (clusterId) params.set("clusterId", clusterId);
    if (branchOfficeId) params.set("branchOfficeId", branchOfficeId);
    if (birthStartDate) params.set("birthStartDate", birthStartDate);
    if (birthEndDate) params.set("birthEndDate", birthEndDate);
    if (registerStartDate) params.set("registerStartDate", `${registerStartDate}T00:00:00`);
    if (registerEndDate) params.set("registerEndDate", `${registerEndDate}T23:59:59`);

    return params.toString();
  }

  static async getClientsList(
    pagination: number,
    filters: {
      idNumber?: string;
      externalId?: string;
      segmentId?: string;
      levelId?: string;
      clusterId?: string;
      branchOfficeId?: string;
      birthStartDate?: string;
      birthEndDate?: string;
      registerStartDate?: string;
      registerEndDate?: string;
    },
    size: number = 10
  ) {
    const filterQuery = BrightMarketApi.buildClientListFilterQuery(filters);
    const params = new URLSearchParams(filterQuery);
    params.set("page", String(zeroBasedPage(pagination)));
    params.set("size", String(size));

    const response = await client.get(`/api/clients/list?${params.toString()}`, {
      headers: {
        Authorization: getToken(),
      },
    });

    const clients = response.data.data?.content || [];
    return { clients };
  }

  static async getClientsListCount(filters: {
    idNumber?: string;
    externalId?: string;
    segmentId?: string;
    levelId?: string;
    clusterId?: string;
    branchOfficeId?: string;
    birthStartDate?: string;
    birthEndDate?: string;
    registerStartDate?: string;
    registerEndDate?: string;
  }) {
    const filterQuery = BrightMarketApi.buildClientListFilterQuery(filters);
    const url = filterQuery
      ? `/api/clients/list/count?${filterQuery}`
      : "/api/clients/list/count";

    const response = await client.get(url, {
      headers: {
        Authorization: getToken(),
      },
    });

    return { totalElements: response.data.data?.totalElements ?? 0 };
  }

  static async getClientsForList(
    organizationId: string,
    pagination: number,
    filters: {
      idNumber?: string;
      externalId?: string;
      segmentId?: string;
      levelId?: string;
      clusterId?: string;
      branchOfficeId?: string;
      birthStartDate?: string;
      birthEndDate?: string;
      registerStartDate?: string;
      registerEndDate?: string;
    }
  ) {
    const listResponse = await BrightMarketApi.getClientsList(pagination, filters);
    const countResponse = await BrightMarketApi.getClientsListCount(filters);
    return {
      _embedded: { clients: listResponse.clients },
      totalElements: countResponse.totalElements,
    };
  }

  static async exportClientsToCsv(
    organizationId: string,
    filters: {
      idNumber?: string;
      segmentId?: string;
      levelId?: string;
      birthStartDate?: string;
      birthEndDate?: string;
      registerStartDate?: string;
      registerEndDate?: string;
    }
  ) {
    const {
      idNumber,
      segmentId,
      levelId,
      birthEndDate,
      birthStartDate,
      registerStartDate,
      registerEndDate,
    } = filters;

    const response = await client.get(
      `/api/clients/export?${idNumber ? `idNumber=${idNumber}&` : ""}
      ${levelId ? `levelId=${levelId}&` : ""}${
        segmentId ? `segmentId=${segmentId}&` : ""
      }${birthStartDate ? `birthStartDate=${birthStartDate}&` : ""}${
        birthEndDate ? `birthEndDate=${birthEndDate}&` : ""
      }${
        registerEndDate ? `registerEndDate=${registerEndDate}&` : ""
      }${
        registerStartDate
          ? `registerStartDate=${registerStartDate}&`
          : ""
      }`,
      {
        headers: {
          Authorization: getToken(),
        },
        responseType: 'blob',
      }
    );

    return response;
  }

  static async deleteClient(clientId: number) {
    const response = await client.delete(`/api/clients/${clientId}`, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response;
  }

  static async createClient(clientInfo: {
    idNumber: string;
    birthdate: string;
    documentType: string;
    country: string;
    city: string;
    sex: string;
    active: boolean;
    levelId?: string;
    segments?: string[];
    name?: string;
    lastName?: string;
    phone?: string;
    mobile?: string;
    address?: string;
    email?: string;
    termsConditions?: boolean;
    storeOnlyContactValidation?: boolean;
  }) {
    const {
      idNumber,
      birthdate,
      documentType,
      country,
      city,
      sex,
      active,
      levelId,
      segments,
      name,
      lastName,
      phone,
      mobile,
      address,
      email,
      termsConditions,
      storeOnlyContactValidation,
    } = clientInfo;
    const formData = new FormData();
    formData.append("idNumber", idNumber);
    if (birthdate) formData.append("birthdate", birthdate);
    if (documentType) formData.append("idTypeId", documentType);
    if (country) formData.append("countryId", country);
    if (city) formData.append("stateId", city);
    if (sex) formData.append("sex", sex);
    if (levelId != null) formData.append("levelId", levelId);
    if (segments != null && segments.length > 0) {
      formData.append("segmentIds", segments.join(","));
    }
    if (name) formData.append("name", name);
    if (lastName) formData.append("lastName", lastName);
    if (phone) formData.append("phone", phone);
    if (mobile) formData.append("mobile", mobile);
    if (address) formData.append("address", address);
    if (email) formData.append("email", email);
    formData.append("active", active.toString());
    if (termsConditions != null) {
      formData.append("termsConditions", termsConditions.toString());
    }
    if (storeOnlyContactValidation != null) {
      formData.append(
        "storeOnlyContactValidation",
        storeOnlyContactValidation.toString()
      );
    }

    const response = await client.post("/api/clients/", formData, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response;
  }

  static async editClient(clientInfo: {
    id: number;
    externalId?: string;
    birthdate?: string;
    documentType?: string;
    documentNumber?: string;
    country?: string;
    city?: string;
    sex?: string;
    active?: boolean;
    levelId?: string;
    name?: string;
    lastName?: string;
    phone?: string;
    mobile?: string;
    address?: string;
    email?: string;
    termsConditions?: boolean;
    storeOnlyContactValidation?: boolean;
  }) {
    const {
      id,
      documentType,
      documentNumber,
      country,
      city,
      sex,
      birthdate,
      active,
      levelId,  
      externalId,
      name,
      lastName,
      phone,
      mobile,
      address,
      email,
      termsConditions,
      storeOnlyContactValidation,
    } = clientInfo;
    const formData = new FormData();

    if (documentNumber != null) formData.append("idNumber", documentNumber);
    if (active != null) formData.append("active", active.toString());
    if (documentType != null) formData.append("idTypeId", String(documentType));
    if (country != null) formData.append("countryId", String(country));
    if (city != null) formData.append("stateId", String(city));
    if (sex != null) formData.append("sex", String(sex));
    if (birthdate != null) formData.append("birthdate", String(birthdate));
    if (levelId != null) formData.append("levelId", String(levelId));
    if (name != null) formData.append("name", String(name));
    if (lastName != null) formData.append("lastName", String(lastName));
    if (phone != null) formData.append("phone", String(phone));
    if (mobile != null) formData.append("mobile", String(mobile));
    if (address != null) formData.append("address", String(address));
    if (email != null) formData.append("email", String(email));
    if (externalId != null) formData.append("externalId", String(externalId));
    if (termsConditions != null) {
      formData.append("termsConditions", termsConditions.toString());
    }
    if (storeOnlyContactValidation != null) {
      formData.append(
        "storeOnlyContactValidation",
        storeOnlyContactValidation.toString()
      );
    }

    const response = await client.put(`/api/clients/${id}`, formData, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response;
  }

  static async getClientById(clientId: string) {
    const response = await client.get(`/api/clients/${clientId}`, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response.data;
  }

  static async getProductsRecommendationsByClientId(
    organizationId: string,
    pagination: number,
    id: string
  ) {
    const response = await client.get(
      `/api/recommendations/search/findByClientId?clientId=${id}&organizationId=${organizationId}&projection=productRecommendationProjection&size=10&page=${
        zeroBasedPage(pagination)
      }`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    const recommendations = response.data._embedded.recommendations;
    const { totalElements } = response.data.page;

    return { recommendations, totalElements };
  }

  static async deleteProductRecommendation(
    clientId: string,
    productRecommendationId: number
  ) {
    const response = await client.delete(
      `/api/clients/recommendation/${clientId}/${productRecommendationId}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async getClientMovementsById(clientId: string, pagination: number) {
    const response = await client.get(
      `/api/points/search/findByClientId?projection=pointsMovementProjectionWithoutClient&clientId=${clientId}&page=${
        zeroBasedPage(pagination)
      }&size=20`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response.data._embedded;
  }

  static async addMovementToClient(
    clientId: number,
    movement: {
      type: string;
      pointsMoved: number;
      comments: string;
      expirationDate?: string;
    }
  ) {
    const response = await client.post(
      `/api/clients/${clientId}/points/addPointsMovement`,
      movement,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async getPointsSummaryByClientId(clientId: number) {
    const response = await client.get(
      `/api/clients/${clientId}/points/summary`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response.data.data;
  }
  static async setPointsToClient(
    clientId: number,
    units: { activePoints: number; pendingPoints: number }
  ) {
    const formData = new FormData();
    formData.append("activePoints", units.activePoints.toString());
    formData.append("pendingPoints", units.pendingPoints.toString());

    const response = await client.post(
      `/api/clients/${clientId}/setPoints`,
      formData,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async getClientStatsByClientId(
    organizationId: string,
    clientId: string
  ) {
    const response = await client.get(
      `/api/tickets/stats/by-client?clientId=${clientId}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async getPurchasedItemsByClientId(
    organizationId: string,
    pagination: number,
    clientId: string
  ) {
    const response = await client.get(
      `/api/tickets/purchased-items?clientId=${clientId}&size=5&page=${
        zeroBasedPage(pagination)
      }`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    const purchasedSkus = response.data.purchasedSkus?.content || [];
    const purchasedBrands = response.data.purchasedBrands?.content || [];
    const purchasedCategories = response.data.purchasedCategories?.content || [];
    
    const totalSkus = response.data.purchasedSkus?.totalElements || 0;
    const totalBrands = response.data.purchasedBrands?.totalElements || 0;
    const totalCategories = response.data.purchasedCategories?.totalElements || 0;

    return { 
      purchasedSkus, 
      purchasedBrands, 
      purchasedCategories,
      totalSkus,
      totalBrands,
      totalCategories
    };
  }

  static async getAllClients() {
    const response = await client.get(`/api/clients/getAll`, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response.data;
  }

  static async getValidationStatistics() {
    const response = await client.get(`/api/clients/validation-statistics`, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response.data;
  }

  static async getClientSegments(organizationId: string, pagination: number) {
    const response = await client.get(
      `/api/clients/clientSegmentsWithSize?page=${pagination}&size=10`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    const clientSegments = response.data.data.content;
    const { totalElements } = response.data.data;

    return { clientSegments, totalElements };
  }

  static async deleteClientSegment(clientSegmentId: number) {
    const response = await client.delete(
      `/api/clients/segments/${clientSegmentId}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async createClientSegment(
    segment:
      | {
          name: string;
          description: string;
          typeInt?: number;
          minAge: number;
          maxAge: number;
          type: "DYNAMIC";
          sex: string;
          averageTicketMin: number;
          averageTicketMax: number;
          purchaseChannelId: number;
          purchaseCountPeriod: number;
          purchaseCountLastDays: number;
          purchaseCountRange: number;
          purchaseCountStartDate: string;
          purchaseCountEndDate: string;
          totalSpent: number;
          birthdayStartDate: string;
          birthdayEndDate: string;
          programRegistrationStartDate: string;
          programRegistrationEndDate: string;
          active: boolean;
          articleIds: string[];
          skuId: string; //[]
          brandIds: string[];
          clientLevelId: string;
        }
      | {
          name: string;
          description: string;
          typeInt?: number;
          type: "STATIC";
          active: boolean;
          clientIds: string[];
        }
  ) {
    const response = await client.post("/api/clients/segments/", segment, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response;
  }
  static async previewClientSegment(
    segment: {
      name: string;
      description: string;
      typeInt?: number;
      minAge: number;
      maxAge: number;
      type: "DYNAMIC";
      sex: string;
      averageTicket: number;
      purchaseChannelId: number;
      purchaseCountPeriod: number;
      purchaseCountLastDays: number;
      purchaseCountRange: number;
      purchaseCountStartDate: string;
      purchaseCountEndDate: string;
      totalSpent: number;
      birthdayStartDate: string;
      birthdayEndDate: string;
      programRegistrationStartDate: string;
      programRegistrationEndDate: string;
      active: boolean;
      articleIds: string[];
      skuId: string; //[]
      brandIds: string[];
      clientLevelId: string;
    },
    page: number,
    size: number
  ) {
    const response = await client.post(
      `/api/clients/segments/previewDynamic?page=${
        page - 1
      }&size=${size}&sortBy=id&direction=ASC`,
      segment,
      {
        headers: {
          Authorization: getToken(),
        },
        timeout: 60000,
      }
    );

    return response;
  }

  static async editClientSegment(
    id: number,
    segment:
      | {
          name: string;
          description: string;
          type: "DYNAMIC";
          sex: string;
          minAge: number;
          maxAge: number;
          averageTicketMin: number;
          averageTicketMax: number;
          purchaseChannelId: number;
          purchaseCountPeriod: number;
          purchaseCountLastDays: number;
          purchaseCountRange: number;
          purchaseCountStartDate: string;
          purchaseCountEndDate: string;
          totalSpent: number;
          birthdayStartDate: string;
          birthdayEndDate: string;
          programRegistrationStartDate: string;
          programRegistrationEndDate: string;
          active: boolean;
          articleIds: string[];
          skuId: string; //[]
          brandIds: string[];
          clientLevelId: string;
        }
      | {
          name: string;
          description: string;
          type: "STATIC";
          active: boolean;
          clientIds: string[];
        }
  ) {
    const formData = new FormData();
    formData.append("name", segment.name);
    formData.append("description", segment.description);
    formData.append("active", segment.active.toString());

    if (segment.type === "STATIC") {
      if (segment.type) formData.append("type", "STATIC");
      formData.append("clientIds", segment.clientIds.toString());
    }
    if (segment.type === "DYNAMIC") {
      if (segment.type) formData.append("type", "DYNAMIC");
      if (segment.minAge) formData.append("minAge", segment.minAge.toString());
      if (segment.maxAge) formData.append("maxAge", segment.maxAge.toString());
      if (segment.sex) formData.append("sex", segment.sex);
      if (segment.averageTicketMin)
        formData.append("averageTicketMin", segment.averageTicketMin.toString());
      if (segment.averageTicketMax)
        formData.append("averageTicketMax", segment.averageTicketMax.toString());
      if (segment.purchaseCountPeriod)
        formData.append(
          "purchaseCountPeriod",
          segment.purchaseCountPeriod.toString()
        );
      if (segment.purchaseCountLastDays)
        formData.append(
          "purchaseCountLastDays",
          segment.purchaseCountLastDays.toString()
        );
      if (segment.purchaseCountRange)
        formData.append(
          "purchaseCountRange",
          segment.purchaseCountRange.toString()
        );
      if (segment.purchaseCountStartDate)
        formData.append(
          "purchaseCountStartDate",
          segment.purchaseCountStartDate
        );
      if (segment.purchaseCountEndDate)
        formData.append("purchaseCountEndDate", segment.purchaseCountEndDate);
      if (segment.totalSpent)
        formData.append("totalSpent", segment.totalSpent.toString());
      if (segment.birthdayStartDate)
        formData.append("birthdayStartDate", segment.birthdayStartDate);
      if (segment.birthdayEndDate)
        formData.append("birthdayEndDate", segment.birthdayEndDate);
      if (segment.programRegistrationStartDate)
        formData.append(
          "programRegistrationStartDate",
          segment.programRegistrationStartDate
        );
      if (segment.programRegistrationEndDate)
        formData.append(
          "programRegistrationEndDate",
          segment.programRegistrationEndDate
        );
      formData.append("brandsIds", segment.brandIds.toString());
    }

    const response = await client.put(`/api/clients/segments/${id}`, formData, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response;
  }

  static async cloneClientSegment(clientSegmentId: number) {
    const formData = new FormData();
    formData.append("copyText", "copia");

    const response = await client.post(
      `/api/clients/segments/clone/${clientSegmentId}`,
      formData,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async getClientSegmentById(
    clientSegmentId: string,
    clientPage: number
  ) {
    const response = await client.get(
      `/api/clients/segments/${clientSegmentId}?page=${
        clientPage - 1
      }&size=10&sortBy=id&direction=ASC`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async getClientsBySegmentId(
    clientSegmentId: string,
    pagination: number,
    size: number = 10
  ) {
    const response = await client.get(
      `/api/clients/segments/${clientSegmentId}?page=${pagination}&size=${size}&sortBy=id&direction=ASC`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response.data;
  }

  static async addClientsToClientSegment(
    clientSegmentId: number,
    clientId: number
  ) {
    const formData = new FormData();
    formData.append("clientId", clientId.toString());

    const response = await client.put(
      `/api/clients/segments/${clientSegmentId}/addClient`,
      formData,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    return response;
  }

  static async deleteClientFromClientSegment(
    clientSegmentId: number,
    clientId: number
  ) {
    const formData = new FormData();
    formData.append("clientId", clientId.toString());

    const response = await client.put(
      `/api/clients/segments/${clientSegmentId}/deleteClient`,
      formData,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    return response;
  }

  static async getPromotions(organizationId: string, pagination: number, filters?: { name?: string; externalId?: string; status?: string; type?: string; origin?: string; brandId?: number }) {
    let url = `/api/promotions/search/findByFilters?organizationId=${organizationId}&projection=promotionBasicProjection&size=10&page=${
      zeroBasedPage(pagination)
    }`;
    
    if (filters) {
      if (filters.name) url += `&name=${encodeURIComponent(filters.name)}`;
      if (filters.externalId) url += `&externalId=${encodeURIComponent(filters.externalId)}`;
      if (filters.status) url += `&status=${encodeURIComponent(filters.status)}`;
      if (filters.type) url += `&type=${encodeURIComponent(filters.type)}`;
      if (filters.origin) url += `&origin=${encodeURIComponent(filters.origin)}`;
      if (filters.brandId) url += `&brandId=${encodeURIComponent(filters.brandId)}`;
    }

    const response = await client.get(url, {
      headers: {
        Authorization: getToken(),
      },
    });

    const promotions = response.data._embedded;
    const { totalElements } = response.data.page;

    return { promotions, totalElements };
  }

  static async getPromotionsUsedByClientId(
    organizationId: string,
    pagination: number,
    clientId: string
  ) {
    const response = await client.get(
      `/api/promotions/search/findDistinctPromotionsByClientIdAndOrganizationId?clientId=${clientId}&organizationId=${organizationId}&projection=promotionProjection&size=10&page=${
        zeroBasedPage(pagination)
      }`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    const promotions = response.data._embedded.promotions;
    const { totalElements } = response.data.page;

    return { promotions, totalElements };
  }

  static async getPromotionsNoDate(organizationId: string, pagination: number) {
    const response = await client.get(
      `/api/promotions/search/findStartDateIsNull?organizationId=${organizationId}&projection=promotionProjection&size=10&page=${
        zeroBasedPage(pagination)
      }`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    const promotions = response.data._embedded;
    const { totalElements } = response.data.page;

    return { promotions, totalElements };
  }

  static async getPromotionsByMonth(month: string) {
    const response = await client.get(
      `/api/promotions/monthByPromotion/${month}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    return response.data;
  }

  static async deletePromotion(promotionId: number) {
    const response = await client.delete(`/api/promotions/${promotionId}`, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response;
  }

  static async getPromotionById(promotionId: string) {
    const response = await client.get(`/api/promotions/${promotionId}`, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response.data;
  }

  static async getPromotionRecommendationContext(promotionId: string) {
    const response = await client.get(
      `/api/promotions/${promotionId}/recommendation-context`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response.data;
  }

  static async completePromotionFromRecommendation(
    promotionId: string,
    body: {
      name?: string;
      description?: string;
      startDate?: string;
      endDate?: string;
      skuIds?: number[];
      branchOfficeIds?: number[];
      clientSegmentIds?: number[];
    }
  ) {
    const response = await client.post(
      `/api/promotions/${promotionId}/complete-from-recommendation`,
      body,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response.data;
  }

  static async getRegularPromotionSkus(
    promotionId: string,
    pagination: number,
    name?: string
  ) {
    const response = await client.get(
      `/api/promotions/${promotionId}/skus?page=${pagination}&size=10${
        name ? `&name=${encodeURIComponent(name)}` : ""
      }`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    const skus = response.data.data.content;
    const totalElements = response.data.data.totalElements;
    return { skus, totalElements };
  }

  static async createPromotionBuyTogether(
    name: string,
    externalId: string,
    description: string,
    recurrence: any,
    startDate: any,
    endDate: any,
    discountFirstList: string,
    discountSecondList: string,
    skuIdFirstList: any,
    skuIdSecondList: any,
    branchOfficeIds: any,
    clientSegmentIds: any,
    categoryIds: any,
    brandIds: any,
    acceptedPaymentMethodIds: any,
    bines: any,
    binMin: number,
    binMax: number,
    minimumTicket: number,
    maximumTicket: number,
    quantityApplications: number,
    quantityPerArticle: number,
    quantityPerBranchOffice: number,
    quantityPerClient: number,
    accumulateOtherPromotions: boolean,
    distributeLines: boolean
  ) {
    const body = {
      promotion: {
        name,
        externalId,
        description,
        recurrence,
        startDate,
        endDate,
        branchOfficeIds,
        clientSegmentIds,
        categoryIds,
        brandIds,
        acceptedPaymentMethodIds,
        bines,
        binMin,
        binMax,
        minimumTicket,
        maximumTicket,
        quantityApplications,
        quantityPerArticle,
        quantityPerBranchOffice,
        quantityPerClient,
        accumulateOtherPromotions,
        distributeLines,
        type: "BUY_TOGETHER",
      },
      discountFirstList,
      discountSecondList,
      skuIdFirstList,
      skuIdSecondList,
    };

    const response = await client.post("/api/promotions/buyTogether", body, {
      headers: {
        Authorization: getToken(),
      },
    });
    return response;
  }

  static async createPromotionMxN(
    name: string,
    externalId: string,
    description: string,
    recurrence: any,
    startDate: any,
    endDate: any,
    skuIds: any,
    quantityForPromotion: string,
    quantityWithDiscount: string,
    discountPercent: string,
    allProducts: boolean,
    highPrice: boolean,
    forTotalArticles: boolean,
    branchOfficeIds: any,
    clientSegmentIds: any,
    categoryIds: any,
    brandIds: any,
    acceptedPaymentMethodIds: any,
    bines: any,
    binMin: number,
    binMax: number,
    minimumTicket: number,
    maximumTicket: number,
    quantityApplications: number,
    quantityPerArticle: number,
    quantityPerBranchOffice: number,
    quantityPerClient: number,
    accumulateOtherPromotions: boolean,
    distributeLines: boolean
  ) {
    const body = {
      promotion: {
        name,
        externalId,
        description,
        recurrence,
        startDate,
        endDate,
        branchOfficeIds,
        clientSegmentIds,
        categoryIds,
        brandIds,
        acceptedPaymentMethodIds,
        bines,
        binMin,
        binMax,
        minimumTicket,
        maximumTicket,
        quantityApplications,
        quantityPerArticle,
        quantityPerBranchOffice,
        quantityPerClient,
        accumulateOtherPromotions,
        distributeLines,
        type: "BUY_TOGETHER",
      },
      quantityForPromotion,
      quantityWithDiscount,
      discountPercent,
      allProducts,
      highPrice,
      forTotalArticles,
      skuIds,
    };

    const response = await client.post("/api/promotions/mxn", body, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response;
  }

  static async createPromotionRegular(promoInfo: {
    name: string;
    externalId: string;
    description: string;
    recurrence: any;
    startDate: any;
    endDate: any;
    regularPromotionType: string;
    branchOfficeIds: any;
    clientSegmentIds: any;
    categoryIds: any;
    brandIds: any;
    acceptedPaymentMethodIds: any;
    bines: any;
    binMin: number;
    binMax: number;
    skuIds: any;
    minimumTicket: number;
    maximumTicket: number;
    quantityApplications: number;
    quantityPerArticle: number;
    quantityPerBranchOffice: number;
    quantityPerClient: number;
    accumulateOtherPromotions: boolean;
    distributeLines: boolean;
    discountValue?: string;
    discountPercentage?: any;
    giftId?: string;
  }) {
    const {
      name,
      description,
      externalId,
      recurrence,
      startDate,
      endDate,
      regularPromotionType,
      branchOfficeIds,
      clientSegmentIds,
      categoryIds,
      brandIds,
      acceptedPaymentMethodIds,
      bines,
      binMin,
      binMax,
      skuIds,
      minimumTicket,
      maximumTicket,
      quantityApplications,
      quantityPerArticle,
      quantityPerBranchOffice,
      quantityPerClient,
      accumulateOtherPromotions,
      distributeLines,
      discountValue,
      discountPercentage,
      giftId,
    } = promoInfo;

    const body = {
      promotion: {
        name,
        externalId,
        description,
        recurrence,
        startDate,
        endDate,
        branchOfficeIds,
        clientSegmentIds,
        categoryIds,
        brandIds,
        acceptedPaymentMethodIds,
        bines,
        binMin,
        binMax,
        minimumTicket,
        maximumTicket,
        quantityApplications,
        quantityPerArticle,
        quantityPerBranchOffice,
        quantityPerClient,
        accumulateOtherPromotions,
        distributeLines,
      },
      skuIds,
      regularPromotionType,
      giftId: giftId ?? null,
      discountPercent: discountPercentage ?? null,
      discountValue: discountValue ?? null,
    };

    const response = await client.post("/api/promotions/regular", body, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response;
  }

  static async createPromotionBogo(
    name: string,
    externalId: string,
    description: string,
    recurrence: any,
    startDate: any,
    endDate: any,
    skuIds: any,
    discountType: string,
    giftId: string,
    branchOfficeIds: any,
    clientSegmentIds: any,
    categoryIds: any,
    brandIds: any,
    acceptedPaymentMethodIds: any,
    bines: any,
    binMin: number,
    binMax: number,
    minimumTicket: number,
    maximumTicket: number,
    quantityApplications: number,
    quantityPerArticle: number,
    quantityPerBranchOffice: number,
    quantityPerClient: number,
    accumulateOtherPromotions: boolean,
    distributeLines: boolean
  ) {
    const body = {
      promotion: {
        name,
        externalId,
        description,
        recurrence,
        startDate,
        endDate,
        branchOfficeIds,
        clientSegmentIds,
        categoryIds,
        brandIds,
        acceptedPaymentMethodIds,
        bines,
        binMin,
        binMax,
        minimumTicket,
        maximumTicket,
        quantityApplications,
        quantityPerArticle,
        quantityPerBranchOffice,
        quantityPerClient,
        accumulateOtherPromotions,
        distributeLines,
        type: "BOGO",
      },
      type: discountType,
      skuIds,
      giftId,
    };
    const response = await client.post("/api/promotions/buyOneGetOne", body, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response;
  }

  static async createPromotionProgressive(
    name: string,
    externalId: string,
    description: string,
    recurrence: any,
    startDate: any,
    endDate: any,
    skuIds: any,
    lines: any,
    branchOfficeIds: any,
    clientSegmentIds: any,
    categoryIds: any,
    brandIds: any,
    acceptedPaymentMethodIds: any,
    bines: any,
    binMin: number,
    binMax: number,
    minimumTicket: number,
    maximumTicket: number,
    quantityApplications: number,
    quantityPerArticle: number,
    quantityPerBranchOffice: number,
    quantityPerClient: number,
    accumulateOtherPromotions: boolean,
    distributeLines: boolean
  ) {
    const body = {
      promotion: {
        name,
        externalId,
        description,
        recurrence,
        startDate,
        endDate,
        branchOfficeIds,
        clientSegmentIds,
        categoryIds,
        brandIds,
        acceptedPaymentMethodIds,
        bines,
        binMin,
        binMax,
        minimumTicket,
        maximumTicket,
        quantityApplications,
        quantityPerArticle,
        quantityPerBranchOffice,
        quantityPerClient,
        accumulateOtherPromotions,
        distributeLines,
      },
      skuIds,
      lines,
    };
    const response = await client.post(
      "/api/promotions/progressiveDiscount",
      body,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async editPromotionRegular(
    id: number,
    promo: {
      promotion: {
        name: string;
        externalId: string;
        description: string;
        recurrence: any;
        startDate: any;
        endDate: any;
        branchOfficeIds: any;
        clientSegmentIds: any;
        categoryIds: any;
        brandIds: any;
        acceptedPaymentMethodIds: any;
        bines: any;
        binMin: number;
        binMax: number;
        minimumTicket: number;
        maximumTicket: number;
        quantityApplications: number;
        quantityPerArticle: number;
        quantityPerBranchOffice: number;
        quantityPerClient: number;
        accumulateOtherPromotions: boolean;
        distributeLines: boolean;
      };
      discountValue?: string;
      discountPercent?: any;
      giftId?: string;
      regularPromotionType: string;
      skuIds: any;
    }
  ) {
    const response = await client.put(`/api/promotions/regular/${id}`, promo, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response;
  }

  static async editPromotionBuyTogether(
    id: number,
    promo: {
      promotion: {
        name: string;
        externalId: string;
        description: string;
        recurrence: any;
        startDate: any;
        endDate: any;
        branchOfficeIds: any;
        clientSegmentIds: any;
        categoryIds: any;
        brandIds: any;
        acceptedPaymentMethodIds: any;
        bines: any;
        binMin: number;
        binMax: number;
        minimumTicket: number;
        maximumTicket: number;
        quantityApplications: number;
        quantityPerArticle: number;
        quantityPerBranchOffice: number;
        quantityPerClient: number;
        accumulateOtherPromotions: boolean;
        distributeLines: boolean;
      };
      discountFirstList: number;
      discountSecondList: number;
      skuIdFirstList: any;
      skuIdSecondList: any;
    }
  ) {
    const response = await client.put(
      `/api/promotions/buyTogether/${id}`,
      promo,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async editPromotionMxN(
    id: number,
    promo: {
      promotion: {
        name: string;
        externalId: string;
        description: string;
        recurrence: any;
        startDate: any;
        endDate: any;
        branchOfficeIds: any;
        clientSegmentIds: any;
        categoryIds: any;
        brandIds: any;
        acceptedPaymentMethodIds: any;
        bines: any;
        binMin: number;
        binMax: number;
        minimumTicket: number;
        maximumTicket: number;
        quantityApplications: number;
        quantityPerArticle: number;
        quantityPerBranchOffice: number;
        quantityPerClient: number;
        accumulateOtherPromotions: boolean;
        distributeLines: boolean;
      };
      discountPercent: number;
      quantityWithDiscount: number;
      quantityForPromotion: number;
      allProducts: boolean;
      forTotalArticles: boolean;
      highPrice: boolean;
      skuIds: any;
    }
  ) {
    const response = await client.put(`/api/promotions/mxn/${id}`, promo, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response;
  }

  static async editPromotionProgressive(
    id: number,
    promo: {
      promotion: {
        name: string;
        externalId: string;
        description: string;
        recurrence: any;
        startDate: any;
        endDate: any;
        branchOfficeIds: any;
        clientSegmentIds: any;
        categoryIds: any;
        brandIds: any;
        acceptedPaymentMethodIds: any;
        bines: any;
        binMin: number;
        binMax: number;
        minimumTicket: number;
        maximumTicket: number;
        quantityApplications: number;
        quantityPerArticle: number;
        quantityPerBranchOffice: number;
        quantityPerClient: number;
        accumulateOtherPromotions: boolean;
        distributeLines: boolean;
      };
      lines: any;
      skuIds: any;
    }
  ) {
    const response = await client.put(
      `/api/promotions/progressiveDiscount/${id}`,
      promo,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async editPromotionBogo(
    id: number,
    promo: {
      promotion: {
        name: string;
        externalId: string;
        description: string;
        recurrence: any;
        startDate: any;
        endDate: any;
        branchOfficeIds: any;
        clientSegmentIds: any;
        categoryIds: any;
        brandIds: any;
        acceptedPaymentMethodIds: any;
        bines: any;
        binMin: number;
        binMax: number;
        minimumTicket: number;
        maximumTicket: number;
        quantityApplications: number;
        quantityPerArticle: number;
        quantityPerBranchOffice: number;
        quantityPerClient: number;
        accumulateOtherPromotions: boolean;
        distributeLines: boolean;
      };
      type: string;
      skuIds: any;
    }
  ) {
    const response = await client.put(
      `/api/promotions/buyOneGetOne/${id}`,
      promo,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async getBrands(
    organizationId: string,
    pagination: number,
    filters: { name: string; externalId: string }
  ) {
    const { name, externalId } = filters;

    const response = await client.get(
      `/api/brands/search/findByOrganizationId?organizationId=${organizationId}&projection=brandProjection&size=10&page=${
        zeroBasedPage(pagination)
      }${name ? `&name=${name}` : ""}${
        externalId ? `&externalId=${externalId}` : ""
      }`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    const brands = response.data._embedded;
    const { totalElements } = response.data.page;

    return { brands, totalElements };
  }

  static async deleteBrand(brandId: number) {
    const response = await client.delete(`/api/brands/${brandId}`, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response;
  }

  static async createBrand(
    name: string,
    externalId: string,
    description: string
  ) {
    const formData = new FormData();
    formData.append("name", name);
    if (externalId) formData.append("externalId", externalId);
    formData.append("description", description);

    const response = await client.post("/api/brands/", formData, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response;
  }

  static async editBrand(
    brandId: number,
    name: string,
    externalId: string,
    description: string
  ) {
    const formData = new FormData();
    formData.append("name", name);
    formData.append("externalId", externalId);
    formData.append("description", description);

    const response = await client.put(`/api/brands/${brandId}`, formData, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response;
  }

  static async getAllIdTypes() {
    const response = await client.get(
      `/api/idTypes/search/getAll?projection=idTypeProjection`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response.data._embedded;
  }

  static async getAllSexTypes() {
    const response = await client.get(`/api/types/sex`, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response.data;
  }

  static async getMessageTemplateStatuses() {
    const response = await client.get(`/api/types/messageTemplateStatuses`, {
      headers: {
        Authorization: getToken(),
      },
    });
    return response.data;
  }

  static async getMessageTemplateLanguages() {
    const response = await client.get(`/api/types/messageTemplateLanguages`, {
      headers: {
        Authorization: getToken(),
      },
    });
    return response.data;
  }

  static async getMessageTemplateCategories() {
    const response = await client.get(`/api/types/messageTemplateCategories`, {
      headers: {
        Authorization: getToken(),
      },
    });
    return response.data;
  }

  static async getAllCountries() {
    const response = await client.get(
      `/api/countries/search/getAll?projection=countryProjection`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response.data._embedded;
  }

  static async getAllCountriesWithExistingClients(organizationId: string) {
    const response = await client.get(
      `/api/countries/search/findCountriesWithClientsByOrganization?organizationId=${organizationId}&projection=countryProjection`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response.data._embedded;
  }

  static async getAllCitiesWithExistingClients(organizationId: string) {
    const response = await client.get(
      `/api/states/search/findStaesWithClientsByOrganization?organizationId=${organizationId}&projection=stateProjection`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response.data._embedded;
  }

  static async getAllStatesByCountry(countryId: number) {
    const response = await client.get(
      `/api/states/search/findByCountryId?countryId=${countryId}&projection=stateWithoutProjection`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response.data._embedded;
  }

  static async getTickets(
    organizationId: string,
    pagination: number,
    filters: {
      code?: string;
      branchOfficeId?: number;
      clusterId?: number;
      startDate?: string;
      endDate?: string;
      skuIds?: number[];
      minTotal?: number;
      maxTotal?: number;
      documentType?: string;
      clientId?: number;
    }
  ) {
    const {
      code,
      branchOfficeId,
      clusterId,
      startDate,
      endDate,
      skuIds,
      minTotal,
      maxTotal,
      documentType,
      clientId,
    } = filters;
    const response = await client.get(
      `/api/tickets/search/query?organizationId=${organizationId}&projection=ticketProjection&size=10&page=${pagination}${
        code ? `&code=${code}` : ""
      }${branchOfficeId ? `&branchOfficeId=${branchOfficeId}` : ""}${
        clusterId ? `&clusterId=${clusterId}` : ""
      }${startDate ? `&startDate=${startDate}` : ""}${
        endDate ? `&endDate=${endDate}` : ""
      }${minTotal ? `&minTotal=${minTotal}` : ""}${
        maxTotal ? `&maxTotal=${maxTotal}` : ""
      }${skuIds && skuIds?.length > 0 ? `&skuIds=${skuIds}` : ""}${
        documentType ? `&documentType=${documentType}` : ""
      }${clientId ? `&clientId=${clientId}` : ""
      }`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    const tickets = response.data._embedded;
    const { totalElements } = response.data.page;

    return { tickets, totalElements };
  }

  static async getTicketsByClientId(
    organizationId: string,
    pagination: number,
    clientId: string,
    clusterId?: number
  ) {
    const response = await client.get(
      `/api/tickets/search/byClient?clientId=${clientId}&organizationId=${organizationId}&projection=ticketProjection&size=10&page=${
        zeroBasedPage(pagination)
      }${clusterId ? `&clusterId=${clusterId}` : ""}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    const tickets = response.data._embedded;
    const { totalElements } = response.data.page;

    return { tickets, totalElements };
  }

  static async getTicketById(ticketId: string) {
    const response = await client.get(`/api/tickets/${ticketId}`, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response.data;
  }

  static async deleteTicket(ticketId: number) {
    const response = await client.delete(`/api/tickets/${ticketId}`, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response;
  }

  static async getAllTicketDocumentTypes() {
    const response = await client.get(`/api/types/ticketDocumentTypes`, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response.data;
  }

  static async createTicket(ticketInfo: any) {
    const response = await client.post("/api/tickets", ticketInfo, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response;
  }

  static async schedulePromotion(id: number, startDate: any, endDate: any) {
    const formData = new FormData();
    formData.append("startDate", startDate);
    if (endDate) formData.append("endDate", endDate);
    const response = await client.post(
      `/api/promotions/schedule/${id}`,
      formData,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async cancelPromotion(id: number) {
    const response = await client.post(
      `/api/promotions/cancel/${id}`,
      {},
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async getAllRecurrencesTypes() {
    const response = await client.get(`/api/types/recurrenceTypes`, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response.data;
  }

  static async getPricings(
    organizationId: string,
    pagination: number,
    filters: {
      categoryId?: string;
      brandId?: number;
      branchOfficeId?: number;
      articleId?: string;
      articleGroupId?: number;
    }
  ) {
    const { brandId, categoryId, branchOfficeId, articleId, articleGroupId } =
      filters;
    const response = await client.get(
      `/api/pricings/search/findPricingsWithFilters?organizationId=${organizationId}&projection=pricingProjection&size=10&page=${
        zeroBasedPage(pagination)
      }${categoryId ? `&categoryId=${categoryId}` : ""}${
        brandId ? `&brandId=${brandId}` : ""
      }${articleId ? `&articleId=${articleId}` : ""}${
        articleGroupId ? `&articleGroupId=${articleGroupId}` : ""
      }${branchOfficeId ? `&branchOfficeId=${branchOfficeId}` : ""}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    const pricings = response.data._embedded;
    const { totalElements } = response.data.page;

    return { pricings, totalElements };
  }

  static async getHistoricPricings(
    organizationId: string,
    filters: {
      page: number;
      articleId?: number;
      brandId?: number;
      categoryId?: number;
    }
  ) {
    const { page, articleId, brandId, categoryId } = filters;
    const response = await client.get(
      `/api/pricings/queryHistoricPricing?organizationId=${organizationId}&projection=pricingProjection&page=${
        page - 1
      }&size=10${articleId ? "&articleId=" + articleId : ""}${
        brandId ? "&brandId=" + brandId : ""
      }${categoryId ? "&categoryId=" + categoryId : ""}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    const pricings = response.data.data.content;
    const { totalElements } = response.data.data;
    return { pricings, totalElements };
  }

  static async deletePricing(pricingId: number) {
    const response = await client.delete(`/api/pricings/${pricingId}`, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response;
  }

  static async getPricingsByMonth(month: string) {
    const response = await client.get(
      `/api/pricings/monthByPricing/${month}`,
      // `/api/promotions/monthByPromotion/2022-05-11`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    return response.data;
  }

  static async schedulePricing(id: number, startDate: any, endDate: any) {
    const formData = new FormData();
    formData.append("startDate", startDate);
    if (endDate) formData.append("endDate", endDate);
    const response = await client.post(
      `/api/pricings/schedule/${id}`,
      formData,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async getAllPricingTypes() {
    const response = await client.get(`/api/pricingTypes/getAll`, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response.data;
  }

  static async getAllEndPricingTypes() {
    const response = await client.get(`/api/types/endPricingTypes`, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response.data;
  }

  static async createPricing(pricing: any) {
    const response = await client.post("/api/pricings/", pricing, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response;
  }

  static async getPricingsNoDate(organizationId: string, pagination: number) {
    const response = await client.get(
      `/api/pricings/search/findStartDateIsNull?organizationId=${organizationId}&projection=pricingProjection&size=10&page=${
        zeroBasedPage(pagination)
      }`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    const pricings = response.data._embedded;
    const { totalElements } = response.data.page;

    return { pricings, totalElements };
  }

  static async getPricingById(pricingId: string) {
    const response = await client.get(`/api/pricings/${pricingId}`, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response.data;
  }

  static async getPricingTimeLineById(pricingId: string) {
    const response = await client.get(
      `/api/pricings/${pricingId}/changesOfPrice`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response.data;
  }

  static async editPricing(id: number, pricing: any) {
    const response = await client.put(`/api/pricings/${id}`, pricing, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response;
  }

  static async getSupplierPaymentMethods(
    organizationId: string,
    pagination: number
  ) {
    const response = await client.get(
      `/api/supplierPaymentMethod/search/findByOrganization?organizationId=${organizationId}&projection=supplierPaymentMethodProjection&size=10&page=${
        zeroBasedPage(pagination)
      }`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    const supplierPaymentMethods = response.data._embedded;
    const { totalElements } = response.data.page;

    return { supplierPaymentMethods, totalElements };
  }

  static async createSupplierPaymentMethod(paymentMethod: {
    name: string;
    paymentDeadlineDays: number;
    typeId?: number;
  }) {
    const { name, paymentDeadlineDays, typeId } = paymentMethod;
    const response = await client.post(
      "/api/supplierPaymentMethod/",
      { name, paymentDeadlineDays, typeId },
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async deleteSupplierPaymentMethod(id: number) {
    const response = await client.delete(`/api/supplierPaymentMethod/${id}`, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response;
  }

  static async editSupplierPaymentMethod(
    id: number,
    name: string,
    paymentDeadlineDays: number,
    typeId?: number
  ) {
    const response = await client.put(
      `/api/supplierPaymentMethod/${id}`,
      { name, paymentDeadlineDays, typeId },
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    return response;
  }

  static async getAllSupplierPaymentMethods() {
    const response = await client.get(`/api/supplierPaymentMethod/getAll`, {
      headers: {
        Authorization: getToken(),
      },
    });
    return response.data;
  }

  static async getPurchaseUnits(organizationId: string, pagination: number) {
    const response = await client.get(
      `/api/purchaseUnits/search/findByOrganization?organizationId=${organizationId}&projection=purchaseUnitProjection&size=10&page=${
        zeroBasedPage(pagination)
      }`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    const purchaseUnits = response.data._embedded;
    const { totalElements } = response.data.page;
    return { purchaseUnits, totalElements };
  }

  static async createPurchaseUnit(
    purchaseUnit: string,
    conversionFactor: string
  ) {
    const response = await client.post(
      "/api/purchaseUnits/",
      { purchaseUnit, conversionFactor },
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async deletePurchaseUnit(purchaseUnitId: number) {
    const response = await client.delete(
      `/api/purchaseUnits/${purchaseUnitId}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async editPurchaseUnit(
    purchaseUnitId: number,
    purchaseUnit: string,
    conversionFactor: string
  ) {
    const response = await client.put(
      `/api/purchaseUnits/${purchaseUnitId}`,
      { purchaseUnit, conversionFactor },
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async discontinueArticle(articleId: number) {
    const response = await client.put(
      `/api/articles/toggleDiscontinued/${articleId}`,
      {},
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async discontinueBranchOfficeArticle(
    branchOfficeId: number,
    articleId: number
  ) {
    const response = await client.put(
      `/api/branchOffices/${branchOfficeId}/toggleUnsupply/${articleId}`,
      {},
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }
  static async getExcelTemplate(type: string) {
    const response = await client.get(`/api/${type}/excelTemplate`, {
      responseType: "arraybuffer",
      headers: {
        Authorization: getToken(),
        ContentType: "blob",
      },
    });
    return response;
  }

  static async processExcelTemplate(type: string, file: any) {
    const formData = new FormData();
    formData.append("file", file);

    const response = await client.post(
      `/api/${type}/processExcelTemplate`,
      formData,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    return response;
  }

  static async getNotifications(pagination: number) {
    const response = await client.get(
      `/api/notifications/getByUser?size=10&page=${zeroBasedPage(pagination)}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    const notifications = response.data.data.content;
    const { totalElements } = response.data.data;
    return { notifications, totalElements };
  }

  static async readNotification(notificationId: number) {
    const response = await client.put(
      `/api/notifications/read/${notificationId}`,
      {},
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async unreadNotification(notificationId: number) {
    const response = await client.put(
      `/api/notifications/unread/${notificationId}`,
      {},
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async getSeasons(pagination: number, organizationId: string) {
    const response = await client.get(
      `/api/seasons/search/findByOrganizationId?organizationId=${organizationId}&projection=seasonsProjection&page=${pagination}&size=10`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    const { seasons } = response.data._embedded;
    const { totalElements } = response.data.page;
    return { seasons, totalElements };
  }

  static async getSeasonsFiltered(name: string) {
    const response = await client.get(
      `/api/seasons/query?name=${name}&page=0&size=10`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response.data;
  }

  static async getSeasonById(seasonId: string) {
    const response = await client.get(`/api/seasons/${seasonId}`, {
      headers: {
        Authorization: getToken(),
      },
    });
    return response.data;
  }

  static async editSeason(
    seasonId: string,
    name: string,
    startingDate: string,
    endingDate: string
  ) {
    const formData = new FormData();
    formData.append("name", name);
    formData.append("startingDate", startingDate);
    formData.append("endingDate", endingDate);

    const response = await client.put(`/api/seasons/${seasonId}`, formData, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response;
  }

  static async createSeason(
    name: string,
    startingDate: string,
    endingDate: string
  ) {
    const formData = new FormData();
    formData.append("name", name);
    formData.append("startingDate", startingDate);
    formData.append("endingDate", endingDate);

    const response = await client.post("/api/seasons/", formData, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response;
  }

  static async deleteSeason(id: number) {
    const response = await client.delete(`/api/seasons/${id}`, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response;
  }

  static async getCommercialDates(pagination: number, organizationId: string) {
    const response = await client.get(
      `/api/commercialDates/search/findByOrganizationId?organizationId=${organizationId}&projection=commercialDateProjection&page=${pagination}&size=10`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    const { commercialDates } = response.data._embedded;
    const { totalElements } = response.data.page;
    return { commercialDates, totalElements };
  }

  static async createCommercialDates(
    name: string,
    startingDate: string,
    endingDate: string,
    channelIds?: number[]
  ) {
    const formData = new FormData();
    formData.append("name", name);
    formData.append("startingDate", startingDate);
    formData.append("endingDate", endingDate);
    channelIds?.forEach((id) => formData.append("channelIds", id.toString()));

    const response = await client.post("/api/commercialDates/", formData, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response;
  }

  static async getCommercialDateById(commercialDateId: string) {
    const response = await client.get(
      `/api/commercialDates/${commercialDateId}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    return response.data;
  }

  static async editCommercialDate(
    commercialDateId: string,
    name: string,
    channelIds?: number[]
  ) {
    const formData = new FormData();
    formData.append("name", name);
    channelIds?.forEach((id) => formData.append("channelIds", id.toString()));

    const response = await client.put(
      `/api/commercialDates/${commercialDateId}`,
      formData,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async deleteCommercialDate(id: number) {
    const response = await client.delete(`/api/commercialDates/${id}`, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response;
  }

  static async getCommercialDateEditionsForYear(year: number) {
    const response = await client.get(
      `/api/commercialDates/editionsForYear?year=${year}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    return response.data;
  }

  static async getCommercialDateEditions(commercialDateId: number) {
    const response = await client.get(
      `/api/commercialDates/${commercialDateId}/editions`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    return response.data;
  }

  static async addCommercialDateEdition(
    commercialDateId: number,
    year: number,
    startingDate: string,
    endingDate: string
  ) {
    const formData = new FormData();
    formData.append("year", String(year));
    formData.append("startingDate", startingDate);
    formData.append("endingDate", endingDate);

    const response = await client.post(
      `/api/commercialDates/${commercialDateId}/editions`,
      formData,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async editCommercialDateEdition(
    commercialDateId: number,
    editionId: number,
    startingDate: string,
    endingDate: string
  ) {
    const formData = new FormData();
    formData.append("startingDate", startingDate);
    formData.append("endingDate", endingDate);

    const response = await client.put(
      `/api/commercialDates/${commercialDateId}/editions/${editionId}`,
      formData,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async deleteCommercialDateEdition(
    commercialDateId: number,
    editionId: number
  ) {
    const response = await client.delete(
      `/api/commercialDates/${commercialDateId}/editions/${editionId}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async getStockMovements(
    organizationId: string,
    filters: {
      pagination: number;
      businessUnitId: string;
      startDate?: string | null;
      endDate?: string | null;
      articleId?: string | null;
      type?: string | null;
      branchOfficeId?: string;
      depositId?: string;
    }
  ) {
    const {
      pagination,
      businessUnitId,
      startDate,
      endDate,
      articleId,
      type,
      depositId,
      branchOfficeId,
    } = filters;

    const response = await client.get(
      `/api/stockMovements/search/filterStockMovement?organizationId=${organizationId}&businessUnitId=${businessUnitId}&size=10&page=${pagination}${
        startDate ? "&startDate=" + startDate + "T00:00:00" : ""
      }${endDate ? "&endDate=" + endDate + "T23:59:59" : ""}${
        articleId ? "&articleId=" + articleId : ""
      }${type ? "&type=" + type : ""}${
        branchOfficeId ? "&branchOfficeId=" + branchOfficeId : ""
      }${depositId ? "&depositId=" + depositId : ""}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    const stockMovements = response.data._embedded.stockMovements;
    const { totalElements } = response.data.page;

    return { stockMovements, totalElements };
  }

  static async getMovementStockTypes() {
    const response = await client.get(`/api/stockMovements/types`, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response.data;
  }

  static async createStockMovements(movement: {
    skuBranchOfficeId?: number;
    skuDepositId?: number;
    stock: number;
    reason: string;
    date: string;
    type: string;
  }) {
    const response = await client.post(
      "/api/stockMovements/",
      { ...movement },
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async createColour(colour: string) {
    const response = await client.post(
      "/api/colours/",
      { name: colour },
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async getColours(
    organizationId: string,
    pagination: number,
    filters: { name: string }
  ) {
    const { name } = filters;

    const response = await client.get(
      `/api/colours/search/findByOrganizationId?organizationId=${organizationId}&size=10&page=${
        zeroBasedPage(pagination)
      }${name ? `&name=${name}` : ""}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    const colours = response.data._embedded.colours;
    const { totalElements } = response.data.page;
    return { colours, totalElements };
  }

  static async getColoursByName(name: string, organizationId: string) {
    const response = await client.get(
      `/api/colours/search/findByNameContainingAndOrganizationId?organizationId=${organizationId}&projection=colourProjection&name=${name}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    const { colours } = response.data._embedded;
    const { totalElements } = response.data.page;

    return { colours, totalElements };
  }

  static async deleteColour(id: number) {
    const response = await client.delete(`/api/colours/${id}`, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response;
  }

  static async editColour(id: number, name: string) {
    const formData = new FormData();
    formData.append("name", name);
    const response = await client.put(`/api/colours/${id}`, formData, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response;
  }

  static async getClusters(organizationId: string, pagination: number) {
    const response = await client.get(
      `/api/clusters/search/findByOrganizationId?organizationId=${organizationId}&size=10&page=${
        zeroBasedPage(pagination)
      }`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    const clusters = response.data._embedded.clusters;
    const { totalElements } = response.data.page;
    return { clusters, totalElements };
  }

  static async editCluster(
    id: number,
    cluster: {
      name: string;
    }
  ) {
    const formData = new FormData();
    formData.append("name", cluster.name);
    const response = await client.put(`/api/clusters/${id}`, formData, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response;
  }

  static async addDepositToCluster(clusterId: number, depositId: number) {
    const formData = new FormData();
    formData.append("depositId", depositId.toString());

    const response = await client.post(
      `/api/clusters/${clusterId}/addDeposit`,
      formData,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async addBranchOfficeToCluster(
    clusterId: number,
    branchOfficeId: number
  ) {
    const formData = new FormData();
    formData.append("branchOfficeId", branchOfficeId.toString());

    const response = await client.post(
      `/api/clusters/${clusterId}/addBranchOffice`,
      formData,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }
  static async deletePlaceFromCluster(placeId: number) {
    const response = await client.delete(
      `/api/clusters/removeBranchOfficeOrDeposit/${placeId}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async createCluster(cluster: {
    name: string;
    depositsIds: any;
    branchOfficesIds: any;
  }) {
    const { name, depositsIds, branchOfficesIds } = cluster;
    const formData = new FormData();
    formData.append("name", name);
    if (depositsIds) formData.append("depositsIds", depositsIds);
    if (branchOfficesIds) formData.append("branchOfficesIds", branchOfficesIds);
    const response = await client.post("/api/clusters/", formData, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response;
  }

  static async deleteCluster(id: number) {
    const response = await client.delete(`/api/clusters/${id}`, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response;
  }

  static async getClusterById(id: string) {
    const response = await client.get(`/api/clusters/${id}`, {
      headers: {
        Authorization: getToken(),
      },
    });
    return response.data;
  }

  static async getVehicles(
    organizationId: string,
    pagination: number,
    filters: { name?: string },
    projection?: string
  ) {
    const { name } = filters;
    const projectionParam = projection || "vehicleProjection";
    const response = await client.get(
      `/api/vehicles/search/findByOrganizationAndName?organizationId=${organizationId}&size=10&page=${
        zeroBasedPage(pagination)
      }&projection=${projectionParam}${name ? `&name=${name}` : ""}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    const vehicles = response.data._embedded.vehicles;
    const { totalElements } = response.data.page;
    return { vehicles, totalElements };
  }

  static async deleteVehicle(id: number) {
    const response = await client.delete(`/api/vehicles/${id}`, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response;
  }

  static async createVehicle(vehicle: {
    name: string;
    code: string;
    brand: string;
    model: string;
    licensePlate: string;
    type: string;
    maxCapacity: number;
    fuelConsumption: number;
  }) {
    const response = await client.post("/api/vehicles/", vehicle, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response;
  }

  static async getVehicleById(id: string) {
    const response = await client.get(`/api/vehicles/${id}`, {
      headers: {
        Authorization: getToken(),
      },
    });
    return response.data;
  }
  static async deleteDepositFromVehicle(id: number) {
    const response = await client.delete(`/api/vehicles/removeDeposit/${id}`, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response;
  }

  static async deleteBranchOfficeFromVehicle(id: number) {
    const response = await client.delete(
      `/api/vehicles/removeBranchOffice/${id}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async deleteScheduleFromVehicle(
    vehicleId: string,
    scheduleId: number
  ) {
    const response = await client.delete(
      `/api/vehicles/${vehicleId}/removeSchedule/${scheduleId}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async addDepositToVehicle(vehicleId: number, depositId: number) {
    const formData = new FormData();
    formData.append("depositId", depositId.toString());

    const response = await client.post(
      `/api/vehicles/${vehicleId}/addDeposit`,
      formData,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async addBranchOfficeToVehicle(
    vehicleId: number,
    branchOfficeId: number
  ) {
    const formData = new FormData();
    formData.append("branchOfficeId", branchOfficeId.toString());

    const response = await client.post(
      `/api/vehicles/${vehicleId}/addBranchOffice`,
      formData,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async addScheduleToVehicle(schedule: {
    vehicleId: number;
    code: number;
    description: string;
    branchOfficeIdFrom: number | null;
    branchOfficeIdTo: number | null;
    depositIdFrom: number | null;
    depositIdTo: number | null;
    recurrence: any;
  }) {
    const response = await client.post(`/api/vehicles/addSchedule`, schedule, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response;
  }

  static async editVehicle(
    id: number,
    vehicle: {
      name: string;
      code: string;
      brand: string;
      model: string;
      licensePlate: string;
      type: string;
      maxCapacity: string;
      fuelConsumption: string;
    }
  ) {
    const formData = new FormData();
    formData.append("name", vehicle.name);
    formData.append("code", vehicle.code);
    formData.append("brand", vehicle.brand);
    formData.append("model", vehicle.model);
    formData.append("licensePlate", vehicle.licensePlate);
    formData.append("type", vehicle.type);
    if (vehicle.maxCapacity)
      formData.append("maxCapacity", vehicle.maxCapacity);
    if (vehicle.fuelConsumption)
      formData.append("fuelConsumption", vehicle.fuelConsumption);
    const response = await client.put(`/api/vehicles/${id}`, formData, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response;
  }

  static async createCurve(curve: {
    name: string;
    sizeStart: number;
    sizeEnd: number[];
    distribution: number[];
  }) {
    const response = await client.post("/api/curves/", curve, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response;
  }

  static async getCurves(
    organizationId: string,
    pagination: number,
    filters: { name?: string }
  ) {
    const { name } = filters;
    const response = await client.get(
      `/api/curves/search/findByOrganizationAndName?organizationId=${organizationId}&size=10&page=${
        zeroBasedPage(pagination)
      }&projection=curveProjection${name ? `&name=${name}` : ""}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    const curves = response.data._embedded.curves;
    const { totalElements } = response.data.page;
    return { curves, totalElements };
  }

  static async deleteCurve(id: number) {
    const response = await client.delete(`/api/curves/${id}`, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response;
  }

  static async getCurveById(id: string) {
    const response = await client.get(`/api/curves/${id}`, {
      headers: {
        Authorization: getToken(),
      },
    });
    return response.data;
  }

  static async editCurve(
    id: number,
    curve: {
      name: string;
      sizeStart: number;
      sizeEnd: number[];
      distribution: number[];
    }
  ) {
    const formData = new FormData();
    formData.append("name", curve.name);
    formData.append("sizeStart", curve.sizeStart.toString());
    formData.append("sizeEnd", curve.sizeEnd.toString());
    formData.append("distribution", curve.distribution.toString());

    const response = await client.put(`/api/curves/${id}`, formData, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response;
  }

  static async addCurveToArticle(articleId: number, curveId: number) {
    const formData = new FormData();
    formData.append("curveId", curveId.toString());

    const response = await client.post(
      `/api/articles/${articleId}/addCurve`,
      formData,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async deleteCurveFromArticle(articleId: number, curveId: number) {
    const response = await client.delete(
      `/api/articles/${articleId}/removeCurve/${curveId}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async getHistoricalStockBranchOffice(
    organizationId: string,
    branchOfficeId: string,
    pagination: number,
    filters: { startDate?: string; endDate?: string }
  ) {
    const { startDate, endDate } = filters;
    const response = await client.get(
      `/api/skuBranchOfficeHistoricStock/search/findByOrganizationIdAndBranchOfficeIdAndDateRange?organizationId=${organizationId}&branchOfficeId=${branchOfficeId}&size=10&page=${
        zeroBasedPage(pagination)
      }&projection=skuBranchOfficeHistoricStockProjection${
        startDate ? `&startDate=${startDate}` : ""
      }${endDate ? `&endDate=${endDate}` : ""}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    const stocks = response.data._embedded.skuBranchOfficeHistoricStock;
    const { totalElements } = response.data.page;
    return { stocks, totalElements };
  }

  static async getHistoricalStockDeposit(
    organizationId: string,
    depositId: string,
    pagination: number,
    filters: { startDate?: string; endDate?: string }
  ) {
    const { startDate, endDate } = filters;
    const response = await client.get(
      `/api/skuDepositHistoricStock/search/findByOrganizationIdAndDepositIdAndDateRange?organizationId=${organizationId}&depositId=${depositId}&size=10&page=${
        zeroBasedPage(pagination)
      }&projection=skuBranchOfficeHistoricStockProjection${
        startDate ? `&startDate=${startDate}` : ""
      }${endDate ? `&endDate=${endDate}` : ""}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    const stocks = response.data._embedded.skuDepositHistoricStock;
    const { totalElements } = response.data.page;
    return { stocks, totalElements };
  }

  static async ruleCreateRepositionOrdersForStock0() {
    const response = await client.post(
      `/api/rules/createRepositionOrdersForStock0`,
      {},
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async ruleCreateRepositionOrdersForStockMin() {
    const response = await client.post(
      `/api/rules/createRepositionOrdersForStockMin`,
      {},
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );

    return response;
  }

  static async getClientLevels(
    organizationId: string,
    pagination: number,
    filters: { name?: string; level?: string; origin?: string }
  ) {
    const { name, level, origin } = filters;
    const response = await client.get(
      `/api/clientLevel/search/findByFilters?size=10&page=${
        zeroBasedPage(pagination)
      }${name ? `&name=${name}` : ""}${
        level ? `&level=${level}` : ""
      }${origin ? `&origin=${origin}` : ""}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    const clientLevels = response.data.data.content;
    const { totalElements } = response.data.data;
    return { clientLevels, totalElements };
  }

  static async getClientLevelsForList(
    organizationId: string,
    pagination: number,
    filters: { name?: string; level?: string; origin?: string }
  ) {
    const { name, level, origin } = filters;
    const response = await client.get(
      `/api/clientLevel/search/findByFilters?size=10&page=${
        zeroBasedPage(pagination)
      }${name ? `&name=${name}` : ""}${
        level ? `&level=${level}` : ""
      }${origin ? `&origin=${origin}` : ""}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    const clientLevels = response.data.data.content;
    const { totalElements } = response.data.data;
    return { clientLevels, totalElements };
  }

  static async deleteClientLevel(id: number) {
    const response = await client.delete(`/api/clientLevel/${id}`, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response;
  }

  static async getAccumulationUnits(
    pagination: number,
    filters: { name?: string; code?: string }
  ) {
    const { name, code } = filters;
    let query = `size=10&page=${zeroBasedPage(pagination)}`;
    if (name) query += `&name=${encodeURIComponent(name)}`;
    if (code) query += `&code=${encodeURIComponent(code)}`;
    const response = await client.get(
      `/api/accumulationUnits/search/findByFilters?${query}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    const accumulationUnits = response.data.data.content;
    const { totalElements } = response.data.data;
    return { accumulationUnits, totalElements };
  }

  static async deleteAccumulationUnit(id: number) {
    const response = await client.delete(`/api/accumulationUnits/${id}`, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response;
  }

  static async createAccumulationUnit(unit: {
    name: string;
    code: string;
    baseAccumulationFactor?: number | null;
    specificAccumulationFactor?: number | null;
    accumulationUnitValue?: number | null;
    useAccumulationForPayment: boolean;
    useClientTiers: boolean;
  }) {
    const response = await client.post("/api/accumulationUnits", unit, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response;
  }

  static async updateAccumulationUnit(
    id: number,
    unit: {
      name: string;
      code: string;
      baseAccumulationFactor?: number | null;
      specificAccumulationFactor?: number | null;
      accumulationUnitValue?: number | null;
      useAccumulationForPayment: boolean;
      useClientTiers: boolean;
    }
  ) {
    const response = await client.put(`/api/accumulationUnits/${id}`, unit, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response;
  }

  static async createClientLevel(clientLevel: {
    name: string;
    level: number;
    description: string;
    ruleType: string;
    ruleQuantity: number;
    accumulationFactor: number;
    active: boolean;
  }) {
    const response = await client.post("/api/clientLevel", clientLevel, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response;
  }

  static async getClientLevelById(id: string) {
    const response = await client.get(`/api/clientLevel/${id}`, {
      headers: {
        Authorization: getToken(),
      },
    });
    return response.data;
  }

  static async editClientLevel(
    id: number,
    clientLevel: {
      name: string;
      level: number;
      description: string;
      ruleType: string;
      ruleQuantity: number;
      accumulationFactor: number;
      active: boolean;
    }
  ) {
    const formData = new FormData();
    formData.append("name", clientLevel.name);
    formData.append("level", clientLevel.level.toString());
    formData.append("description", clientLevel.description ?? "");
    if (clientLevel.ruleType) {
      formData.append("ruleType", clientLevel.ruleType);
    }
    formData.append("active", clientLevel.active.toString());
    formData.append("ruleQuantity", clientLevel.ruleQuantity.toString());
    formData.append(
      "accumulationFactor",
      clientLevel.accumulationFactor.toString()
    );

    const response = await client.put(`/api/clientLevel/${id}`, formData, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response;
  }

  static async getArticleGroups(
    organizationId: string,
    pagination: number,
    filters: { name?: string }
  ) {
    const { name } = filters;
    const response = await client.get(
      `/api/articleGroup/search/findByOrganizationIdName?organizationId=${organizationId}&size=10&page=${
        zeroBasedPage(pagination)
      }&projection=articleGroupProjection${name ? `&name=${name}` : ""}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    const articleGroups = response.data._embedded.articleGroup;
    const { totalElements } = response.data.page;
    return { articleGroups, totalElements };
  }

  static async deleteArticleGroup(id: number) {
    const response = await client.delete(`/api/articleGroup/${id}`, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response;
  }

  static async createArticleGroup(articleGroup: {
    name: string;
    description: string;
    categoryId: string;
    brandId: string;
    branchOfficeId: string;
    articleIds: string[];
    state: boolean;
  }) {
    const response = await client.post("/api/articleGroup/", articleGroup, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response;
  }

  static async getArticleGroupById(id: string) {
    const response = await client.get(`/api/articleGroup/${id}`, {
      headers: {
        Authorization: getToken(),
      },
    });
    return response.data;
  }

  static async getArticleGroupCountById(id: string) {
    const response = await client.get(`/api/articleGroup/count/${id}`, {
      headers: {
        Authorization: getToken(),
      },
    });
    return response.data;
  }

  static async editArticleGroup(
    id: number,
    articleGroup: {
      name: string;
      description: string;
      categoryId: string;
      brandId: string;
      branchOfficeId: string;
      articleIds: string[];
      state: boolean;
    }
  ) {
    const formData = new FormData();
    formData.append("name", articleGroup.name);
    formData.append("description", articleGroup.description ?? "");
    formData.append("categoryId", articleGroup.categoryId ?? "");
    formData.append("brandId", articleGroup.brandId ?? "");
    if (articleGroup.articleIds && articleGroup.articleIds.length > 0) {
      articleGroup.articleIds.forEach(articleId => {
        formData.append("articleIds", articleId);
      });
    }
    formData.append("branchOfficeId", articleGroup.branchOfficeId ?? "");
    formData.append("active", articleGroup.state.toString());

    const response = await client.put(`/api/articleGroup/${id}`, formData, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response;
  }

  static async getRewardTypes(
    organizationId: string,
    pagination: number,
    filters: { name?: string; active?: number }
  ) {
    const { name, active } = filters;
    const response = await client.get(
      `/api/rewardType/search/findByOrganizationIdNameAndStatus?organizationId=${organizationId}&size=10&page=${
        zeroBasedPage(pagination)
      }&projection=rewardTypeProjection${name ? `&name=${name}` : ""}${
        active !== undefined ? `&status=${active}` : ""
      }`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    const rewardTypes = response.data._embedded.rewardType;
    const { totalElements } = response.data.page;
    return { rewardTypes, totalElements };
  }

  static async deleteRewardType(id: number) {
    const response = await client.delete(`/api/rewards/type/${id}`, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response;
  }

  static async createRewardType(rewardType: {
    name: string;
    status: boolean;
    values?: { value: number; valueType: string }[];
  }) {
    const response = await client.post("/api/rewards/type", rewardType, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response;
  }

  static async getRewardTypeById(id: string) {
    const response = await client.get(`/api/rewards/type/${id}`, {
      headers: {
        Authorization: getToken(),
      },
    });
    return response.data;
  }

  static async editRewardType(
    id: string,
    rewardType: {
      name: string;
      status: boolean;
      values?: { value: number; valueType: string }[];
    }
  ) {
    const response = await client.put(`/api/rewards/type/${id}`, rewardType, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response;
  }

  static async getRewards(
    organizationId: string,
    pagination: number,
    filters: {
      name?: string;
      active?: boolean;
      dateStart?: string;
      dateEnd?: string;
      type?: number;
      clusterId?: number;
      origin?: string;
    }
  ) {
    const { name, active, dateStart, dateEnd, type, clusterId, origin } = filters;
    let queryParams = `organizationId=${organizationId}&size=10&page=${zeroBasedPage(pagination)}`;

    if (name) queryParams += `&name=${encodeURIComponent(name)}`;
    if (active !== undefined) queryParams += `&active=${active}`;
    if (dateStart) queryParams += `&dateStart=${dateStart}`;
    if (dateEnd) queryParams += `&dateEnd=${dateEnd}`;
    if (type !== undefined) queryParams += `&type=${type}`;
    if (clusterId !== undefined) queryParams += `&clusterId=${clusterId}`;
    if (origin) queryParams += `&origin=${encodeURIComponent(origin)}`;

    const response = await client.get(`/api/rewards/list?${queryParams}`, {
      headers: {
        Authorization: getToken(),
      },
    });
    const data = response.data?.data ?? {};
    return {
      rewards: data.rewards || [],
      totalElements: data.totalElements ?? 0,
    };
  }

  static async deleteReward(id: number) {
    const response = await client.delete(`/api/rewards/${id}`, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response;
  }
  static async createReward(reward: {
    name: string;
    status: boolean;
    rewardType: string;
    description: string;
    value: number;
    cost?: number;
    estimatedReach?: number;
    estimatedImpact?: number;
    estimatedRoi?: number;
    accumulatedImpact?: number;
    accumulatedCost?: number;
    historicalRoi?: number;
    pointsValue: number;
    validityStart: string;
    validityEnd: string;
    codeLength: number;
    codeType: string;
    prefix: string;
    brandIds: any[];
    categoryIds: any[];
    clientSegmentIds: any[];
    clientIds?: any[];
    levelIds: any[];
    articleIds: any[];
    articleGroupIds: any[];
    clusterIds: any[];
    branchOfficeIds: any[];
  }) {
    const response = await client.post("/api/rewards/", reward, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response;
  }

  static async editReward(
    id: string,
    reward: {
      name: string;
      status: boolean;
      rewardType: string;
      description: string;
      value: number;
      cost?: number;
      estimatedReach?: number;
      estimatedImpact?: number;
      estimatedRoi?: number;
      accumulatedImpact?: number;
      accumulatedCost?: number;
      historicalRoi?: number;
      pointsValue: number;
      validityStart: string;
      validityEnd: string;
      codeLength: number;
      codeType: string;
      prefix: string;
      brandIds: any[];
      categoryIds: any[];
      replaceClients?: boolean;
      clientSegmentIds?: any[];
      clientIds?: any[];
      addClientIds?: any[];
      addClientSegmentIds?: any[];
      removeClientIds?: any[];
      levelIds: any[];
      articleIds: any[];
      articleGroupIds: any[];
      clusterIds?: any[];
      branchOfficeIds?: any[];
    }
  ) {
    const formData = new FormData();
    const {
      name,
      status,
      description,
      value,
      cost,
      estimatedReach,
      estimatedImpact,
      estimatedRoi,
      accumulatedImpact,
      accumulatedCost,
      historicalRoi,
      pointsValue,
      validityStart,
      validityEnd,
      codeLength,
      codeType,
      prefix,
      brandIds,
      categoryIds,
      replaceClients,
      levelIds,
      clientSegmentIds,
      clientIds,
      addClientIds,
      addClientSegmentIds,
      removeClientIds,
      articleIds,
      articleGroupIds,
      clusterIds,
      branchOfficeIds,
      rewardType,
    } = reward;

    formData.append("name", name);
    formData.append("status", status ? status.toString() : "false");
    formData.append("typeId", rewardType);

    if (description !== undefined && description !== null) {
      formData.append("description", description);
    }
    if (prefix !== undefined && prefix !== null) {
      formData.append("prefix", prefix);
    }
    if (codeType !== undefined && codeType !== null) {
      formData.append("codeType", codeType);
    }

    if (value !== undefined && value !== null) {
      formData.append("value", value.toString());
    }
    if (cost !== undefined && cost !== null) {
      formData.append("cost", cost.toString());
    }
    if (estimatedReach !== undefined && estimatedReach !== null) {
      formData.append("estimatedReach", estimatedReach.toString());
    }
    if (estimatedImpact !== undefined && estimatedImpact !== null) {
      formData.append("estimatedImpact", estimatedImpact.toString());
    }
    if (estimatedRoi !== undefined && estimatedRoi !== null) {
      formData.append("estimatedRoi", estimatedRoi.toString());
    }
    if (accumulatedImpact !== undefined && accumulatedImpact !== null) {
      formData.append("accumulatedImpact", accumulatedImpact.toString());
    }
    if (accumulatedCost !== undefined && accumulatedCost !== null) {
      formData.append("accumulatedCost", accumulatedCost.toString());
    }
    if (historicalRoi !== undefined && historicalRoi !== null) {
      formData.append("historicalRoi", historicalRoi.toString());
    }
    if (pointsValue !== undefined && pointsValue !== null) {
      formData.append("pointsValue", pointsValue.toString());
    }
    if (codeLength !== undefined && codeLength !== null) {
      formData.append("codeLength", codeLength.toString());
    }

    if (validityStart) formData.append("validityStart", validityStart);
    if (validityEnd) formData.append("validityEnd", validityEnd);

    if (brandIds && Array.isArray(brandIds)) {
      formData.append("brandIds", brandIds.toString());
    }
    if (categoryIds && Array.isArray(categoryIds)) {
      formData.append("categoryIds", categoryIds.toString());
    }
    if (levelIds && Array.isArray(levelIds)) {
      formData.append("levelIds", levelIds.toString());
    }
    if (replaceClients === true) {
      formData.append("replaceClients", "true");
    }
    if (clientSegmentIds && Array.isArray(clientSegmentIds)) {
      formData.append("clientSegmentIds", clientSegmentIds.toString());
    }
    if (clientIds && Array.isArray(clientIds)) {
      formData.append("clientIds", clientIds.toString());
    }
    if (addClientIds && Array.isArray(addClientIds)) {
      formData.append("addClientIds", addClientIds.toString());
    }
    if (addClientSegmentIds && Array.isArray(addClientSegmentIds)) {
      formData.append("addClientSegmentIds", addClientSegmentIds.toString());
    }
    if (removeClientIds && Array.isArray(removeClientIds)) {
      formData.append("removeClientIds", removeClientIds.toString());
    }
    if (articleIds && Array.isArray(articleIds)) {
      formData.append("articleIds", articleIds.toString());
    }
    if (articleGroupIds && Array.isArray(articleGroupIds)) {
      formData.append("articleGroupIds", articleGroupIds.toString());
    }
    if (clusterIds && Array.isArray(clusterIds)) {
      formData.append("clusterIds", clusterIds.toString());
    }
    if (branchOfficeIds && Array.isArray(branchOfficeIds)) {
      formData.append("branchOfficeIds", branchOfficeIds.toString());
    }

    const response = await client.put(`/api/rewards/${id}`, formData, {
      headers: {
        Authorization: getToken(),
      },
    });

    return response;
  }
  static async getRewardById(id: string) {
    const response = await client.get(`/api/rewards/${id}`, {
      headers: {
        Authorization: getToken(),
      },
    });
    return response.data;
  }

  static async getRewardClients(
    rewardId: string,
    params: {
      page?: number;
      size?: number;
      sortBy?: string;
      direction?: string;
      clientId?: string;
      name?: string;
      email?: string;
      idNumber?: string;
      mobile?: string;
    }
  ) {
    const {
      page = 0,
      size = 20,
      sortBy,
      direction = "ASC",
      clientId,
      name,
      email,
      idNumber,
      mobile,
    } = params;
    const searchParams = new URLSearchParams();
    searchParams.append("page", String(page));
    searchParams.append("size", String(size));
    if (direction) searchParams.append("direction", direction);
    if (sortBy) searchParams.append("sortBy", sortBy);
    if (clientId != null) searchParams.append("clientId", String(clientId));
    if (name) searchParams.append("name", name);
    if (email) searchParams.append("email", email);
    if (idNumber) searchParams.append("idNumber", idNumber);
    if (mobile) searchParams.append("mobile", mobile);
    const response = await client.get(
      `/api/rewards/${rewardId}/clients?${searchParams.toString()}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    // Backend wraps page in { message, status, data }; return page payload for callers.
    return response.data?.data ?? response.data;
  }

  /**
   * Adds clients from the given segments to the reward (PUT /api/rewards/:id with addClientSegmentIds).
   * Pass name/status from the page when available to avoid an extra GET.
   */
  static async addSegmentClientsToReward(
    rewardId: string,
    segmentIds: number[],
    rewardFields?: { name?: string; status?: boolean }
  ) {
    if (!segmentIds?.length) return null;
    let name = rewardFields?.name;
    let status = rewardFields?.status;
    if (name === undefined || status === undefined) {
      const res = await this.getRewardById(rewardId);
      const data = res?.data ?? res;
      name = name ?? data?.name ?? "";
      status = status ?? data?.status ?? false;
    }
    const formData = new FormData();
    formData.append("name", String(name ?? ""));
    formData.append("status", String(!!status));
    formData.append("addClientSegmentIds", segmentIds.join(","));
    const response = await client.put(`/api/rewards/${rewardId}`, formData, {
      headers: {
        Authorization: getToken(),
      },
    });
    return response.data?.data ?? response.data;
  }

  static async sendPrompt(prompt: string) {
    try {
      const response = await client.post(
        "/api/ai/v1/prompt",
        {
          prompt,
        },
        {
          headers: {
            Authorization: getToken(),
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.error("Error sending prompt:", error);
      if (error.response) {
        throw new Error(error.response.data);
      } else if (error.request) {
        throw new Error("No response received from server");
      } else {
        throw new Error(error.message);
      }
    }
  }

  static async getSupplierCataloguesBySupplierId(supplierId: number) {
    const response = await client.get(`/api/supplierCatalogue/bySupplier/${supplierId}`, {
      headers: { Authorization: getToken() }
    });
    return response.data;
  }

  static async createSupplierCatalogue(data: any) {
    const response = await client.post(`/api/supplierCatalogue/`, data, {
      headers: {
        Authorization: getToken(),
      },
    });
    return response.data;
  }

  static async editSupplierCatalogue(id: number, data: any) {
    const response = await client.put(`/api/supplierCatalogue/${id}`, data, {
      headers: {
        Authorization: getToken(),
      },
    });
    return response.data;
  }

  static async deleteSupplierCatalogue(id: number) {
    const response = await client.delete(`/api/supplierCatalogue/${id}`, {
      headers: { Authorization: getToken() }
    });
    return response.data;
  }

  static async uploadStockMovementFile(formData: FormData) {
    const response = await client.post(
      "/api/stockMovements/import/upload",
      formData,
      {
        headers: {
          Authorization: getToken(),
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response;
  }

  static async getStockMovementImports(pagination: number, size?: number) {
    const response = await client.get(
      `/api/stockMovements/import?page=${pagination}${
        size != null ? `&size=${size}` : ""
      }`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    return response;
  }

  static async getStockMovementImportProgress(processId: string) {
    const response = await client.get(
      `/api/stockMovements/import/progress/${processId}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    return response;
  }

  static async cancelStockMovementImport(processId: string) {
    const response = await client.post(
      `/api/stockMovements/import/cancel/${processId}`,
      {},
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    return response;
  }

  static async deleteStockMovementImport(processId: string) {
    const response = await client.delete(
      `/api/stockMovements/import/${processId}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    return response;
  }

  static async downloadStockMovementImportErrors(processId: string) {
    const response = await client.get(
      `/api/stockMovements/import/result/${processId}/errors.csv/url`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    return response.data;
  }

  static async downloadOriginalStockMovementFile(processId: string) {
    const response = await client.get(
      `/api/stockMovements/import/result/${processId}/original/url`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    return response.data;
  }

  static async startStockMovementImport(processId: string) {
    const response = await client.post(
      `/api/stockMovements/import/start/${processId}`,
      {},
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    return response;
  }

  static async uploadTicketFile(formData: FormData) {
    const response = await client.post(
      "/api/tickets/import/upload",
      formData,
      {
        headers: {
          Authorization: getToken(),
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response;
  }

  static async getTicketImports(pagination: number, size: number) {
    const response = await client.get(
      `/api/tickets/import?page=${pagination}&size=${size}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    return response;
  }

  static async getTicketImportProgress(processId: string) {
    const response = await client.get(
      `/api/tickets/import/progress/${processId}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    return response;
  }

  static async cancelTicketImport(processId: string) {
    const response = await client.post(
      `/api/tickets/import/cancel/${processId}`,
      {},
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    return response;
  }

  static async deleteTicketImport(processId: string) {
    const response = await client.delete(
      `/api/tickets/import/${processId}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    return response;
  }

  static async downloadTicketImportErrors(processId: string) {
    const response = await client.get(
      `/api/tickets/import/result/${processId}/errors.csv/url`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    return response.data;
  }

  static async downloadOriginalTicketFile(processId: string) {
    const response = await client.get(
      `/api/tickets/import/result/${processId}/original/url`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    return response.data;
  }

  static async previewTicketImportJsonl(
    processId: string,
    page: number,
    size: number,
    search?: string
  ) {
    const params = new URLSearchParams({
      page: String(page),
      size: String(size),
    });
    if (search) {
      params.set("search", search);
    }
    const response = await client.get(
      `/api/tickets/import/result/${processId}/preview?${params.toString()}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    return response;
  }

  static async startTicketImport(processId: string) {
    const response = await client.post(
      `/api/tickets/import/start/${processId}`,
      {},
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    return response;
  }

  static async uploadRewardFile(formData: FormData) {
    const response = await client.post(
      "/api/rewards/import/upload",
      formData,
      {
        headers: {
          Authorization: getToken(),
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response;
  }

  static async getRewardImports(pagination: number, size: number) {
    const response = await client.get(
      `/api/rewards/import?page=${pagination}&size=${size}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    return response;
  }

  static async getRewardImportProgress(processId: string) {
    const response = await client.get(
      `/api/rewards/import/progress/${processId}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    return response;
  }

  static async cancelRewardImport(processId: string) {
    const response = await client.post(
      `/api/rewards/import/cancel/${processId}`,
      {},
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    return response;
  }

  static async deleteRewardImport(processId: string) {
    const response = await client.delete(
      `/api/rewards/import/${processId}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    return response;
  }

  static async downloadRewardImportErrors(processId: string) {
    const response = await client.get(
      `/api/rewards/import/result/${processId}/errors.csv/url`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    return response.data;
  }

  static async downloadOriginalRewardFile(processId: string) {
    const response = await client.get(
      `/api/rewards/import/result/${processId}/original/url`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    return response.data;
  }

  static async startRewardImport(processId: string) {
    const response = await client.post(
      `/api/rewards/import/start/${processId}`,
      {},
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    return response;
  }

  static async uploadPricingFile(formData: FormData) {
    const response = await client.post(
      "/api/pricings/import/upload",
      formData,
      {
        headers: {
          Authorization: getToken(),
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response;
  }

  static async getPricingImports(pagination: number, size: number) {
    const response = await client.get(
      `/api/pricings/import?page=${pagination}&size=${size}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    return response;
  }

  static async getPricingImportProgress(processId: string) {
    const response = await client.get(
      `/api/pricings/import/progress/${processId}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    return response;
  }

  static async cancelPricingImport(processId: string) {
    const response = await client.post(
      `/api/pricings/import/cancel/${processId}`,
      {},
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    return response;
  }

  static async deletePricingImport(processId: string) {
    const response = await client.delete(
      `/api/pricings/import/${processId}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    return response;
  }

  static async downloadPricingImportErrors(processId: string) {
    const response = await client.get(
      `/api/pricings/import/result/${processId}/errors.csv/url`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    return response.data;
  }

  static async downloadOriginalPricingFile(processId: string) {
    const response = await client.get(
      `/api/pricings/import/result/${processId}/original/url`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    return response.data;
  }

  static async startPricingImport(processId: string) {
    const response = await client.post(
      `/api/pricings/import/start/${processId}`,
      {},
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    return response;
  }

  static async addScheduleToBranchOffice(
    branchOfficeId: number,
    schedule: {
      code: number;
      description: string;
      branchOfficeIdTo: number | null;
      depositIdTo: number | null;
      vehicleId: number;
      recurrence: any;
    }
  ) {
    const response = await client.post(
      `/api/branchOffices/${branchOfficeId}/addSchedule`,
      schedule,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    return response;
  }

  static async addScheduleToDeposit(
    depositId: number,
    schedule: {
      code: number;
      description: string;
      branchOfficeIdTo: number | null;
      depositIdTo: number | null;
      vehicleId: number;
      recurrence: any;
    }
  ) {
    const response = await client.post(
      `/api/deposits/${depositId}/addSchedule`,
      schedule,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    return response;
  }

  static async deleteScheduleFromDeposit(
    depositId: number,
    scheduleId: number
  ) {
    const response = await client.delete(
      `/api/deposits/${depositId}/removeSchedule/${scheduleId}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    return response;
  }

  static async deleteScheduleFromBranchOffice(
    branchOfficeId: number,
    scheduleId: number
  ) {
    const response = await client.delete(
      `/api/branchOffices/${branchOfficeId}/removeSchedule/${scheduleId}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    return response;
  }

  // Campaigns API
  static async getCampaigns(organizationId: string, pagination: number) {
    const response = await client.get(
      `/api/campaigns/?page=${zeroBasedPage(pagination)}&size=10`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    return response;
  }

  static async getCampaignById(id: string) {
    const response = await client.get(`/api/campaigns/${id}`, {
      headers: {
        Authorization: getToken(),
      },
    });
    return response;
  }

  static async getCampaignClientSegmentIds(
    campaignId: string | number
  ): Promise<{ id: number; name: string }[]> {
    const response = await client.get(
      `/api/campaigns/${campaignId}/client-segment-ids`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    const data = response.data;
    if (Array.isArray(data)) return data;
    return data?.clientSegmentIds ?? [];
  }

  static async createCampaign(campaign: {
    name: string;
    description?: string;
    validityStartDate?: string;
    validityEndDate?: string;
    promotionId?: number;
    rewardId?: number;
    clientSegmentIds?: number[];
    campaignType?: string;
    campaignStatus?: string;
    estimatedImpact?: number;
    estimatedCost?: number;
    estimatedRoi?: number;
    estimatedAudience?: number;
  }) {
    const response = await client.post(
      "/api/campaigns/",
      {
        name: campaign.name,
        description: campaign.description || null,
        validityStartDate: campaign.validityStartDate || null,
        validityEndDate: campaign.validityEndDate || null,
        promotionId: campaign.promotionId || null,
        rewardId: campaign.rewardId || null,
        clientSegmentIds: campaign.clientSegmentIds || null,
        campaignType: campaign.campaignType || null,
        campaignStatus: campaign.campaignStatus || null,
        estimatedImpact: campaign.estimatedImpact ?? null,
        estimatedCost: campaign.estimatedCost ?? null,
        estimatedRoi: campaign.estimatedRoi ?? null,
        estimatedAudience: campaign.estimatedAudience ?? null,
      },
      {
        headers: {
          Authorization: getToken(),
          "Content-Type": "application/json",
        },
      }
    );
    return response;
  }

  static async updateCampaign(
    id: string,
    campaign: {
      name?: string;
      description?: string;
      validityStartDate?: string;
      validityEndDate?: string;
      promotionId?: number;
      rewardId?: number;
      clientSegmentIds?: number[];
      campaignType?: string;
      campaignStatus?: string;
      estimatedImpact?: number;
      estimatedCost?: number;
      estimatedRoi?: number;
      estimatedAudience?: number;
    }
  ) {
    const response = await client.put(
      `/api/campaigns/${id}`,
      {
        name: campaign.name || null,
        description: campaign.description !== undefined ? campaign.description : null,
        validityStartDate: campaign.validityStartDate || null,
        validityEndDate: campaign.validityEndDate || null,
        promotionId: campaign.promotionId !== undefined ? campaign.promotionId : null,
        rewardId: campaign.rewardId !== undefined ? campaign.rewardId : null,
        clientSegmentIds: campaign.clientSegmentIds || null,
        campaignType: campaign.campaignType ?? null,
        campaignStatus: campaign.campaignStatus ?? null,
        estimatedImpact: campaign.estimatedImpact ?? null,
        estimatedCost: campaign.estimatedCost ?? null,
        estimatedRoi: campaign.estimatedRoi ?? null,
        estimatedAudience: campaign.estimatedAudience ?? null,
      },
      {
        headers: {
          Authorization: getToken(),
          "Content-Type": "application/json",
        },
      }
    );
    return response;
  }

  static async deleteCampaign(id: string) {
    const response = await client.delete(`/api/campaigns/${id}`, {
      headers: {
        Authorization: getToken(),
      },
    });
    return response;
  }

  static async getCampaignExport(id: string) {
    const response = await client.get(`/api/campaigns/${id}/export`, {
      responseType: "arraybuffer",
      headers: {
        Authorization: getToken(),
      },
    });
    return response;
  }

  static async getCampaignAudienceExport(id: string) {
    const response = await client.get(`/api/campaigns/${id}/audience/export`, {
      responseType: "arraybuffer",
      timeout: 300000,
      headers: {
        Authorization: getToken(),
      },
    });
    return response;
  }

  static async getCampaignClients(
    campaignId: string,
    page: number,
    options?: {
      type?: "EMAIL" | "SMS" | "WHATSAPP" | null;
      excludeClientIds?: number[];
      size?: number;
      name?: string;
      email?: string;
      phone?: string;
      idNumber?: string;
    }
  ) {
    const size = options?.size ?? 10;
    const response = await client.post(
      `/api/campaigns/${campaignId}/clients?page=${page - 1}&size=${size}`,
      {
        type: options?.type ?? null,
        excludeClientIds: options?.excludeClientIds || null,
        name: options?.name || null,
        email: options?.email || null,
        phone: options?.phone || null,
        idNumber: options?.idNumber || null,
      },
      {
        headers: {
          Authorization: getToken(),
          "Content-Type": "application/json",
        },
      }
    );
    return response;
  }

  static async sendCampaign(
    campaignId: string,
    clientIds: number[],
    type: "EMAIL" | "SMS" | "WHATSAPP",
    scheduledAt?: string,
    messageBody?: string,
    messageTemplateId?: number,
    templateVariables?: string
  ) {
    const response = await client.post(
      `/api/campaigns/${campaignId}/send`,
      {
        clientIds: clientIds,
        type: type,
        scheduledAt: scheduledAt,
        messageBody: messageBody,
        messageTemplateId: messageTemplateId,
        templateVariables: templateVariables,
      },
      {
        headers: {
          Authorization: getToken(),
          "Content-Type": "application/json",
        },
      }
    );
    return response;
  }

  static async getMessageTemplates(opts?: {
    refresh?: boolean;
    page?: number;
    size?: number;
    status?: string;
  }) {
    const refresh = !!opts?.refresh;
    const page = opts?.page ?? 0;
    const size = opts?.size ?? 10;
    const status = opts?.status;
    const statusParam = status ? `&status=${encodeURIComponent(status)}` : "";
    const response = await client.get(
      `/api/message-templates?refresh=${refresh ? "true" : "false"}&page=${page}&size=${size}${statusParam}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    return response;
  }

  static async getMessageTemplate(id: number) {
    const response = await client.get(`/api/message-templates/${id}`, {
      headers: {
        Authorization: getToken(),
      },
    });
    return response;
  }

  static async createMessageTemplate(payload: {
    name: string;
    language: string;
    category: string;
    body: string;
    submitForWhatsAppApproval?: boolean;
  }) {
    const response = await client.post(`/api/message-templates`, payload, {
      headers: {
        Authorization: getToken(),
        "Content-Type": "application/json",
      },
    });
    return response;
  }

  static async updateMessageTemplate(
    id: number,
    payload: {
      name: string;
      language: string;
      category: string;
      body: string;
      submitForWhatsAppApproval?: boolean;
    }
  ) {
    const response = await client.put(`/api/message-templates/${id}`, payload, {
      headers: {
        Authorization: getToken(),
        "Content-Type": "application/json",
      },
    });
    return response;
  }

  static async submitMessageTemplate(id: number) {
    const response = await client.post(`/api/message-templates/${id}/submit`, null, {
      headers: {
        Authorization: getToken(),
      },
    });
    return response;
  }

  static async refreshMessageTemplate(id: number) {
    const response = await client.post(`/api/message-templates/${id}/refresh`, null, {
      headers: {
        Authorization: getToken(),
      },
    });
    return response;
  }

  static async deleteMessageTemplate(id: number) {
    const response = await client.delete(`/api/message-templates/${id}`, {
      headers: {
        Authorization: getToken(),
      },
    });
    return response;
  }

  static async getCampaignSents(campaignId: string, page: number) {
    const response = await client.get(
      `/api/campaigns/${campaignId}/sent?page=${page - 1}&size=10`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    return response;
  }

  static async getCampaignSentBatches(campaignId: string, page: number) {
    const response = await client.get(
      `/api/campaigns/${campaignId}/sent-batches?page=${page - 1}&size=10`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    return response;
  }

  static async getCampaignSentBatchDetail(
    campaignId: string,
    batchId: string,
    options?: {
      page?: number;
      size?: number;
      status?: string;
      phone?: string;
      client?: string;
    }
  ) {
    const page = options?.page ?? 1;
    const size = options?.size ?? 10;
    const status = options?.status ? `&status=${encodeURIComponent(options.status)}` : "";
    const phone = options?.phone ? `&phone=${encodeURIComponent(options.phone)}` : "";
    const clientQ = options?.client ? `&client=${encodeURIComponent(options.client)}` : "";
    const response = await client.get(
      `/api/campaigns/${campaignId}/sent-batches/${encodeURIComponent(
        batchId
      )}?page=${page - 1}&size=${size}${status}${phone}${clientQ}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    return response;
  }

  static async getActivationActions(
    page: number,
    filters: {
      name?: string;
      status?: string;
      size?: number;
    }
  ) {
    const params = new URLSearchParams();
    params.set("page", String(Math.max(1, page)));
    params.set("size", String(filters.size && filters.size > 0 ? filters.size : 10));
    if (filters.name) params.set("name", filters.name);
    if (filters.status) params.set("status", filters.status);

    const response = await client.get(
      `/api/activation-actions?${params.toString()}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    return response.data;
  }

  static async getActivationAction(id: number) {
    const response = await client.get(`/api/activation-actions/${id}`, {
      headers: {
        Authorization: getToken(),
      },
    });
    return response.data;
  }

  static async getActivationActionClients(id: number, page: number, size: number) {
    const params = new URLSearchParams();
    params.set("page", page.toString());
    params.set("size", size.toString());
    const response = await client.get(
      `/api/activation-actions/${id}/clients?${params.toString()}`,
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    return response.data;
  }

  static async convertActivationActionToCampaign(id: number) {
    const response = await client.post(
      `/api/activation-actions/${id}/convert-to-campaign`,
      {},
      {
        timeout: 180000,
        headers: {
          Authorization: getToken(),
        },
      }
    );
    return response.data;
  }

  static async deleteActivationAction(id: number) {
    const response = await client.delete(`/api/activation-actions/${id}`, {
      headers: {
        Authorization: getToken(),
      },
    });
    return response;
  }

  static async runActivationEngine() {
    const response = await client.post(
      `/api/activation-actions/engine/run`,
      {},
      {
        headers: {
          Authorization: getToken(),
        },
      }
    );
    return response.data;
  }

  // --- Organization Parameters ---

  static async getAverageTicketBuckets() {
    const response = await client.get(`/api/rewards/average-ticket-buckets`, {
      headers: {
        Authorization: getToken(),
      },
    });
    return response.data;
  }

  static async replaceAverageTicketBuckets(body: {
    buckets: Array<{
      min: number | null;
      max: number | null;
      typeValues: Array<{
        rewardTypeId: number;
        minValue: number | null;
        maxValue: number | null;
      }>;
    }>;
  }) {
    const response = await client.put(
      `/api/rewards/average-ticket-buckets`,
      body,
      {
        headers: {
          Authorization: getToken(),
          "Content-Type": "application/json",
        },
      }
    );
    return response.data;
  }

  static async getOrganizationParameters() {
    const response = await client.get(`/api/organization-parameters`, {
      headers: {
        Authorization: getToken(),
      },
    });
    return response.data;
  }

  static async createOrUpdateOrganizationParameter(
    paramKey: string,
    paramValue: string,
    description?: string
  ) {
    const response = await client.post(
      `/api/organization-parameters`,
      { paramKey, value: paramValue, description },
      {
        headers: {
          Authorization: getToken(),
          "Content-Type": "application/json",
        },
      }
    );
    return response.data;
  }

  static async getProductAnalysisLanding(params: {
    search?: string;
    categoryId?: number;
    seasonId?: number;
    brandId?: number;
    branchOfficeId?: number;
    supplierExternalId?: string;
    period?: number;
    stockFilter?: string;
    sortBy?: string;
    sortOrder?: string;
    page?: number;
    size?: number;
  }) {
    const response = await client.get(`/api/products/analysis`, {
      params,
      headers: { Authorization: getToken() },
    });
    return response.data;
  }

  static async getProductAnalysisHeader(id: number) {
    const response = await client.get(`/api/products/analysis/${id}/header`, {
      headers: { Authorization: getToken() },
    });
    return response.data;
  }

  static async getProductAnalysisKpis(
    id: number,
    params: { period?: number; channelId?: number; branchOfficeId?: number }
  ) {
    const response = await client.get(`/api/products/analysis/${id}/kpis`, {
      params,
      headers: { Authorization: getToken() },
    });
    return response.data;
  }

  static async getProductAnalysisDetailBody(
    id: number,
    params: { period?: number; channelId?: number; branchOfficeId?: number }
  ) {
    const response = await client.get(
      `/api/products/analysis/${id}/detail-body`,
      {
        params,
        headers: { Authorization: getToken() },
      }
    );
    return response.data;
  }

  static async getProductAnalysisDetailChart(
    id: number,
    params: { period?: number; channelId?: number; branchOfficeId?: number }
  ) {
    const response = await client.get(`/api/products/analysis/${id}/chart`, {
      params,
      headers: { Authorization: getToken() },
    });
    return response.data;
  }

  static async getProductAnalysisDetailBreakdown(
    id: number,
    params: { period?: number; channelId?: number; branchOfficeId?: number }
  ) {
    const response = await client.get(`/api/products/analysis/${id}/breakdown`, {
      params,
      headers: { Authorization: getToken() },
    });
    return response.data;
  }

  static async getProductSimilarArticles(skuId: number) {
    const response = await client.get(`/api/products/analysis/similar`, {
      params: { skuId },
      headers: { Authorization: getToken() },
    });
    return response.data;
  }


  static async getBrandAnalysisLanding(params: {
    search?: string;
    period?: number;
    channelId?: number;
    branchOfficeId?: number;
    sortBy?: string;
    sortOrder?: string;
  }) {
    const response = await client.get(`/api/brands/analysis`, {
      params,
      headers: { Authorization: getToken() },
    });
    return response.data;
  }

  static async getBrandAnalysisDetail(
    id: number,
    params: { period?: number; channelId?: number; branchOfficeId?: number }
  ) {
    const response = await client.get(`/api/brands/analysis/${id}`, {
      params,
      headers: { Authorization: getToken() },
    });
    return response.data;
  }

  static async getBrandAnalysisDetailCore(
    id: number,
    params: { period?: number; channelId?: number; branchOfficeId?: number }
  ) {
    const response = await client.get(`/api/brands/analysis/${id}/core`, {
      params,
      headers: { Authorization: getToken() },
    });
    return response.data;
  }

  static async getBrandAnalysisDetailChart(
    id: number,
    params: { period?: number; channelId?: number; branchOfficeId?: number }
  ) {
    const response = await client.get(`/api/brands/analysis/${id}/chart`, {
      params,
      headers: { Authorization: getToken() },
    });
    return response.data;
  }

  static async getBrandAnalysisDetailBreakdown(
    id: number,
    params: { period?: number; channelId?: number; branchOfficeId?: number }
  ) {
    const response = await client.get(`/api/brands/analysis/${id}/breakdown`, {
      params,
      headers: { Authorization: getToken() },
    });
    return response.data;
  }

  static async getStoreAnalysisLanding(params: {
    search?: string;
    period?: number;
    sortBy?: string;
    sortOrder?: string;
  }) {
    const response = await client.get(`/api/stores/analysis`, {
      params,
      headers: { Authorization: getToken() },
    });
    return response.data;
  }

  static async getStoreAnalysisDetail(id: number, params: { period?: number }) {
    const response = await client.get(`/api/stores/analysis/${id}`, {
      params,
      headers: { Authorization: getToken() },
    });
    return response.data;
  }

  static async getStoreAnalysisDetailCore(
    id: number,
    params: { period?: number }
  ) {
    const response = await client.get(`/api/stores/analysis/${id}/core`, {
      params,
      headers: { Authorization: getToken() },
    });
    return response.data;
  }

  static async getStoreAnalysisDetailChart(
    id: number,
    params: { period?: number }
  ) {
    const response = await client.get(`/api/stores/analysis/${id}/chart`, {
      params,
      headers: { Authorization: getToken() },
    });
    return response.data;
  }

  static async getStoreAnalysisDetailBreakdown(
    id: number,
    params: { period?: number }
  ) {
    const response = await client.get(`/api/stores/analysis/${id}/breakdown`, {
      params,
      headers: { Authorization: getToken() },
    });
    return response.data;
  }

  static async getChannelAnalysisLanding(params: {
    search?: string;
    channelIds?: number[];
    period?: number;
    sortBy?: string;
    sortOrder?: string;
  }) {
    const searchParams = new URLSearchParams();
    if (params.search) searchParams.append("search", params.search);
    params.channelIds?.forEach((id) =>
      searchParams.append("channelIds", String(id))
    );
    if (params.period) searchParams.append("period", String(params.period));
    if (params.sortBy) searchParams.append("sortBy", params.sortBy);
    if (params.sortOrder) searchParams.append("sortOrder", params.sortOrder);
    const query = searchParams.toString();
    const response = await client.get(`/api/channels/analysis${query ? `?${query}` : ""}`, {
      headers: { Authorization: getToken() },
    });
    return response.data;
  }

  static async getChannelAnalysisDetail(
    id: number,
    params: { period?: number; search?: string }
  ) {
    const response = await client.get(`/api/channels/analysis/${id}`, {
      params,
      headers: { Authorization: getToken() },
    });
    return response.data;
  }

  static async getChannelAnalysisDetailCore(
    id: number,
    params: { period?: number }
  ) {
    const response = await client.get(`/api/channels/analysis/${id}/core`, {
      params,
      headers: { Authorization: getToken() },
    });
    return response.data;
  }

  static async getChannelAnalysisDetailChart(
    id: number,
    params: { period?: number }
  ) {
    const response = await client.get(`/api/channels/analysis/${id}/chart`, {
      params,
      headers: { Authorization: getToken() },
    });
    return response.data;
  }

  static async getChannelAnalysisDetailBreakdown(
    id: number,
    params: { period?: number; search?: string }
  ) {
    const response = await client.get(`/api/channels/analysis/${id}/breakdown`, {
      params,
      headers: { Authorization: getToken() },
    });
    return response.data;
  }

  // --- Category Analysis ---

  static async getCategoryAnalysis(
    period: number,
    channelId?: number,
    branchOfficeId?: number
  ) {
    const params = new URLSearchParams({ period: String(period) });
    if (channelId != null) params.append("channelId", String(channelId));
    if (branchOfficeId != null) params.append("branchOfficeId", String(branchOfficeId));
    const response = await client.get(`/api/category-analysis?${params}`, {
      headers: { Authorization: getToken() },
    });
    return response.data;
  }

  static async getCategoryAnalysisDetail(
    categoryId: number,
    period: number,
    channelId?: number,
    branchOfficeId?: number
  ) {
    const params = new URLSearchParams({ period: String(period) });
    if (channelId != null) params.append("channelId", String(channelId));
    if (branchOfficeId != null) params.append("branchOfficeId", String(branchOfficeId));
    const response = await client.get(`/api/category-analysis/${categoryId}?${params}`, {
      headers: { Authorization: getToken() },
    });
    return response.data;
  }

  static async getCategoryAnalysisDetailCore(
    categoryId: number,
    params: { period?: number; channelId?: number; branchOfficeId?: number }
  ) {
    const response = await client.get(`/api/category-analysis/${categoryId}/core`, {
      params,
      headers: { Authorization: getToken() },
    });
    return response.data;
  }

  static async getCategoryAnalysisDetailChart(
    categoryId: number,
    params: { period?: number; channelId?: number; branchOfficeId?: number }
  ) {
    const response = await client.get(`/api/category-analysis/${categoryId}/chart`, {
      params,
      headers: { Authorization: getToken() },
    });
    return response.data;
  }

  static async getCategoryAnalysisDetailBreakdown(
    categoryId: number,
    params: { period?: number; channelId?: number; branchOfficeId?: number }
  ) {
    const response = await client.get(`/api/category-analysis/${categoryId}/breakdown`, {
      params,
      headers: { Authorization: getToken() },
    });
    return response.data;
  }

  static async getClientAnalysis(
    period: number,
    channelId?: number,
    branchOfficeId?: number
  ) {
    const params = new URLSearchParams({ period: String(period) });
    if (channelId != null) params.append("channelId", String(channelId));
    if (branchOfficeId != null) params.append("branchOfficeId", String(branchOfficeId));
    const response = await client.get(`/api/client-analysis?${params}`, {
      headers: { Authorization: getToken() },
    });
    return response.data;
  }

  static async getActiveSeasons() {
    const response = await client.get(`/api/active-seasons`, {
      headers: { Authorization: getToken() },
    });
    return response.data;
  }

  static async getClientDiagnosis(clientId: number | string) {
    const response = await client.get(
      `/api/client-diagnosis?clientId=${clientId}`,
      {
        headers: { Authorization: getToken() },
      }
    );
    return response.data;
  }

  static async getCommercialIntelligenceDashboard() {
    const response = await client.get(`/api/commercial-intelligence/dashboard`, {
      headers: { Authorization: getToken() },
    });
    return response.data;
  }

  static async getCommercialIntelligenceFeaturedCampaign(period: string) {
    const response = await client.get(
      `/api/commercial-intelligence/dashboard/featured-campaign?period=${period}`,
      {
        headers: { Authorization: getToken() },
      }
    );
    return response.data;
  }

  static async getProductMixAnalysis(
    startDate: string,
    endDate: string,
    channelId?: number,
    branchOfficeId?: number,
    categoryId?: number,
    brandId?: number,
    articleId?: number
  ) {
    const params = new URLSearchParams({ startDate, endDate });
    if (channelId != null) params.append("channelId", String(channelId));
    if (branchOfficeId != null) params.append("branchOfficeId", String(branchOfficeId));
    if (categoryId != null) params.append("categoryId", String(categoryId));
    if (brandId != null) params.append("brandId", String(brandId));
    if (articleId != null) params.append("articleId", String(articleId));
    const response = await client.get(`/api/product-mix-analysis?${params}`, {
      headers: { Authorization: getToken() },
    });
    return response.data;
  }

  static async getDailyControl() {
    const response = await client.get(`/api/daily-control`, {
      headers: { Authorization: getToken() },
    });
    return response.data;
  }

  static async getDailyControlSummary() {
    const response = await client.get(`/api/daily-control/summary`, {
      headers: { Authorization: getToken() },
    });
    return response.data;
  }

  static async getDailyControlGaps() {
    const response = await client.get(`/api/daily-control/gaps`, {
      headers: { Authorization: getToken() },
    });
    return response.data;
  }

  static async getWholesaleFilters() {
    const response = await client.get(`/api/wholesale-analysis/filters`, {
      headers: { Authorization: getToken() },
    });
    return response.data;
  }

  static async getCommercialSummaryCore(
    startDate: string,
    endDate: string,
    channelId?: number,
    branchOfficeId?: number,
    categoryId?: number,
    brandId?: number
  ) {
    const params = new URLSearchParams({ startDate, endDate });
    if (channelId != null) params.append("channelId", String(channelId));
    if (branchOfficeId != null) params.append("branchOfficeId", String(branchOfficeId));
    if (categoryId != null) params.append("categoryId", String(categoryId));
    if (brandId != null) params.append("brandId", String(brandId));
    const response = await client.get(`/api/commercial-summary/core?${params}`, {
      headers: { Authorization: getToken() },
    });
    return response.data;
  }

  static async getCommercialSummaryProducts(
    startDate: string,
    endDate: string,
    channelId?: number,
    branchOfficeId?: number,
    categoryId?: number,
    brandId?: number
  ) {
    const params = new URLSearchParams({ startDate, endDate });
    if (channelId != null) params.append("channelId", String(channelId));
    if (branchOfficeId != null) params.append("branchOfficeId", String(branchOfficeId));
    if (categoryId != null) params.append("categoryId", String(categoryId));
    if (brandId != null) params.append("brandId", String(brandId));
    const response = await client.get(`/api/commercial-summary/products?${params}`, {
      headers: { Authorization: getToken() },
    });
    return response.data;
  }

  static async getCommercialSummaryClients(
    startDate: string,
    endDate: string,
    channelId?: number,
    branchOfficeId?: number,
    categoryId?: number,
    brandId?: number
  ) {
    const params = new URLSearchParams({ startDate, endDate });
    if (channelId != null) params.append("channelId", String(channelId));
    if (branchOfficeId != null) params.append("branchOfficeId", String(branchOfficeId));
    if (categoryId != null) params.append("categoryId", String(categoryId));
    if (brandId != null) params.append("brandId", String(brandId));
    const response = await client.get(`/api/commercial-summary/clients?${params}`, {
      headers: { Authorization: getToken() },
    });
    return response.data;
  }

  static async getCommercialDateAnalysis() {
    const response = await client.get(`/api/commercial-date-analysis`, {
      headers: { Authorization: getToken() },
    });
    return response.data;
  }

  static async getCommercialDateAnalysisEvents() {
    const response = await client.get(`/api/commercial-date-analysis/events`, {
      headers: { Authorization: getToken() },
    });
    return response.data;
  }

  static async getCommercialDateAnalysisAnnualTotals() {
    const response = await client.get(`/api/commercial-date-analysis/annual-totals`, {
      headers: { Authorization: getToken() },
    });
    return response.data;
  }

  static async getCommercialDateAnalysisLandingMetrics(commercialDateId: number) {
    const response = await client.get(
      `/api/commercial-date-analysis/${commercialDateId}/landing-metrics`,
      { headers: { Authorization: getToken() } }
    );
    return response.data;
  }

  static async getWholesaleAnalysis(
    startDate: string,
    endDate: string,
    categoryId?: number,
    brandId?: number,
    wholesalerId?: number
  ) {
    const params = new URLSearchParams({ startDate, endDate });
    if (categoryId != null) params.append("categoryId", String(categoryId));
    if (brandId != null) params.append("brandId", String(brandId));
    if (wholesalerId != null) params.append("wholesalerId", String(wholesalerId));
    const response = await client.get(`/api/wholesale-analysis?${params}`, {
      headers: { Authorization: getToken() },
    });
    return response.data;
  }

  static async getWholesalers() {
    const response = await client.get(`/api/wholesalers`, {
      headers: { Authorization: getToken() },
    });
    return response.data;
  }

  static async getWholesaleReferenceData() {
    const response = await client.get(`/api/wholesalers/reference-data`, {
      headers: { Authorization: getToken() },
    });
    return response.data;
  }

  static async getWholesaleSettings() {
    const response = await client.get(`/api/wholesalers/settings`, {
      headers: { Authorization: getToken() },
    });
    return response.data;
  }

  static async saveWholesaleSettings(identificationModes: string[]) {
    const response = await client.put(
      `/api/wholesalers/settings`,
      { identificationModes },
      { headers: { Authorization: getToken() } }
    );
    return response.data;
  }

  static async getCommercialDateAnalysisDetail(commercialDateId: number, year?: number) {
    const params = new URLSearchParams();
    if (year != null) params.append("year", String(year));
    const qs = params.toString();
    const response = await client.get(
      `/api/commercial-date-analysis/${commercialDateId}${qs ? `?${qs}` : ""}`,
      { headers: { Authorization: getToken() } }
    );
    return response.data;
  }

  static async createWholesaler(body: Record<string, unknown>) {
    const response = await client.post(`/api/wholesalers`, body, {
      headers: { Authorization: getToken() },
    });
    return response.data;
  }

  static async updateWholesaler(id: number, body: Record<string, unknown>) {
    const response = await client.put(`/api/wholesalers/${id}`, body, {
      headers: { Authorization: getToken() },
    });
    return response.data;
  }

  static async deleteWholesaler(id: number) {
    const response = await client.delete(`/api/wholesalers/${id}`, {
      headers: { Authorization: getToken() },
    });
    return response.data;
  }

  static async listFaissIndexes(
    organizationId: number,
    page: number,
    size: number
  ) {
    const response = await client.get(`/api/faiss-indexes`, {
      params: { organizationId, page, size },
      headers: { Authorization: getToken() },
    });
    return response.data;
  }

  static async getActiveFaissIndex(organizationId: number) {
    const response = await client.get(`/api/faiss-indexes/active`, {
      params: { organizationId },
      headers: { Authorization: getToken() },
    });
    return response.data;
  }

  static async setActiveFaissIndex(
    organizationId: number,
    fileId: string | null
  ) {
    const response = await client.put(
      `/api/faiss-indexes/active`,
      { organizationId, fileId },
      {
        headers: {
          Authorization: getToken(),
          "Content-Type": "application/json",
        },
      }
    );
    return response.data;
  }

  static async getProductSimilaritySettings(organizationId: number) {
    const response = await client.get(`/api/faiss-indexes/similarity-settings`, {
      params: { organizationId },
      headers: { Authorization: getToken() },
    });
    return response.data;
  }

  static async updateProductSimilaritySettings(payload: {
    organizationId: number;
    topK: number;
    skipDuplicates: boolean;
    maxDistance: number;
    sameCategory: boolean;
    sameBrand: boolean;
    maxPriceDifferencePerc: number;
  }) {
    const response = await client.put(
      `/api/faiss-indexes/similarity-settings`,
      payload,
      {
        headers: {
          Authorization: getToken(),
          "Content-Type": "application/json",
        },
      }
    );
    return response.data;
  }

  static async uploadFaissIndex(
    organizationId: number,
    file: File,
    onUploadProgress?: (progress: {
      loaded: number;
      total?: number;
      percent: number;
    }) => void
  ) {
    const formData = new FormData();
    formData.append("file", file);
    const response = await client.post(
      `/api/faiss-indexes/upload?organizationId=${organizationId}`,
      formData,
      {
        headers: {
          Authorization: getToken(),
          "Content-Type": "multipart/form-data",
        },
        timeout: 0,
        onUploadProgress: (event) => {
          if (!onUploadProgress) return;
          const total = event.total || file.size || 0;
          const loaded = event.loaded || 0;
          const percent =
            total > 0 ? Math.min(100, Math.round((loaded * 100) / total)) : 0;
          onUploadProgress({ loaded, total, percent });
        },
      }
    );
    return response.data;
  }

  static async deleteFaissIndex(fileId: string) {
    const response = await client.delete(`/api/faiss-indexes/${fileId}`, {
      headers: { Authorization: getToken() },
    });
    return response;
  }

  static async downloadFaissIndex(fileId: string) {
    const response = await client.get(`/api/faiss-indexes/${fileId}`, {
      headers: { Authorization: getToken() },
      responseType: "blob",
      timeout: 0,
    });
    return response;
  }

  static async listDemandForecastArtifacts(
    organizationId: number,
    kind: string,
    page: number,
    size: number
  ) {
    const response = await client.get(`/api/demand-forecast-artifacts`, {
      params: { organizationId, kind, page, size },
      headers: { Authorization: getToken() },
    });
    return response.data;
  }

  static async getDemandForecastArtifactSelection(organizationId: number) {
    const response = await client.get(`/api/demand-forecast-artifacts/selection`, {
      params: { organizationId },
      headers: { Authorization: getToken() },
    });
    return response.data;
  }

  static async uploadDemandForecastArtifact(
    organizationId: number,
    kind: string,
    file: File,
    onUploadProgress?: (progress: {
      loaded: number;
      total?: number;
      percent: number;
    }) => void
  ) {
    const formData = new FormData();
    formData.append("file", file);
    const response = await client.post(
      `/api/demand-forecast-artifacts/upload?organizationId=${organizationId}&kind=${kind}`,
      formData,
      {
        headers: {
          Authorization: getToken(),
          "Content-Type": "multipart/form-data",
        },
        timeout: 0,
        onUploadProgress: (event) => {
          if (!onUploadProgress) return;
          const total = event.total || file.size || 0;
          const loaded = event.loaded || 0;
          const percent =
            total > 0 ? Math.min(100, Math.round((loaded * 100) / total)) : 0;
          onUploadProgress({ loaded, total, percent });
        },
      }
    );
    return response.data;
  }

  static async setActiveDemandForecastArtifact(
    organizationId: number,
    kind: string,
    fileId: string | null
  ) {
    const response = await client.put(
      `/api/demand-forecast-artifacts/active`,
      { organizationId, kind, fileId },
      {
        headers: {
          Authorization: getToken(),
          "Content-Type": "application/json",
        },
      }
    );
    return response.data;
  }

  static async renameDemandForecastArtifact(
    fileId: string,
    originalFilename: string
  ) {
    const response = await client.patch(
      `/api/demand-forecast-artifacts/${fileId}`,
      { originalFilename },
      {
        headers: {
          Authorization: getToken(),
          "Content-Type": "application/json",
        },
      }
    );
    return response.data;
  }

  static async deleteDemandForecastArtifact(fileId: string) {
    const response = await client.delete(
      `/api/demand-forecast-artifacts/${fileId}`,
      {
        headers: { Authorization: getToken() },
      }
    );
    return response;
  }

  static async downloadDemandForecastArtifact(fileId: string) {
    const response = await client.get(
      `/api/demand-forecast-artifacts/${fileId}`,
      {
        headers: { Authorization: getToken() },
        responseType: "blob",
        timeout: 0,
      }
    );
    return response;
  }
}
